// frontend/src/components/modules/almuerzos/AlmuerzosModule.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { X, Check, Mail, Send, Pencil, Lock, ClipboardList, CheckCircle } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import almuerzosService from '../../../api/almuerzosService';
import SearchableSelect from '../../shared/SearchableSelect';

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];
const DIAS_CENA   = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
const TIPOS_MENU  = [
  { key: 'Almuerzo',            label: 'Almuerzo Semanal',       dias: DIAS_SEMANA },
  { key: 'Cena',                label: 'Cena',                   dias: DIAS_CENA   },
  { key: 'AlmuerzoFinDeSemana', label: 'Almuerzo Fin de Semana', dias: DIAS_SEMANA },
];
const RRHH_ROLES = ['dths_director','dths_gerente','dths_analista','dths_coordinador','admin'];

// ── CORRECCIÓN: getSemanaActual ahora es consistente con getLunesDeSemana ──
// Semana 1 = semana que contiene el primer lunes del año
const getSemanaActual = () => {
  const d = new Date();
  const año = d.getFullYear();
  const primerDia = new Date(año, 0, 1);
  const dow = primerDia.getDay(); // 0=Dom, 1=Lun, ...
  const diasHastaPrimerLunes = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
  const primerLunes = new Date(año, 0, 1 + diasHastaPrimerLunes);
  const diffDias = Math.floor((d - primerLunes) / 86400000);
  if (diffDias < 0) return 1; // Antes del primer lunes del año → semana 1
  return Math.floor(diffDias / 7) + 1;
};

const getLunesDeSemana = (semana, año) => {
  const primerDia = new Date(año, 0, 1);
  const dow = primerDia.getDay();
  const diasHastaPrimerLunes = dow === 0 ? 1 : dow === 1 ? 0 : 8 - dow;
  const primerLunes = new Date(año, 0, 1 + diasHastaPrimerLunes);
  const lunes = new Date(primerLunes);
  lunes.setDate(primerLunes.getDate() + (semana - 1) * 7);
  return lunes;
};

// "YYYY-MM-DD" en hora local (toISOString usa UTC: después de las 8:00 p. m. da el día siguiente)
const isoLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Lunes a domingo de la semana indicada (la cena incluye sábado y domingo)
const rangoSemana = (semana, año) => {
  const lunes = getLunesDeSemana(semana, año);
  const domingo = new Date(lunes);
  domingo.setDate(lunes.getDate() + 6);
  return { fechaInicio: isoLocal(lunes), fechaFin: isoLocal(domingo) };
};

const fmtFecha = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
const fechaLocalDesdeISO = (isoStr) => {
  if (!isoStr) return '';
  return isoStr.replace(/T.*$/, '').trim();
};
const calcDias = (fechaInicioStr, diasNombres) => {
  const [y, m, d] = fechaInicioStr.replace(/T.*$/, '').split('-').map(Number);
  return diasNombres.map((nombre, i) => {
    const ref = new Date(y, m - 1, d + i);
    const yyyy = ref.getFullYear();
    const mm = String(ref.getMonth() + 1).padStart(2, '0');
    const dd = String(ref.getDate()).padStart(2, '0');
    return { nombre, fecha: `${yyyy}-${mm}-${dd}` };
  });
};
const fmtD = d => {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), dd = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${dd}`;
};

// ── Exportación ──────────────────────────────────────────────
const exportToCSV = (data, filename) => {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g, '""')}"`).join(','));
  const csv = [headers.join(','), ...rows].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename + '.csv'; a.click();
  URL.revokeObjectURL(url);
};

const exportToExcel = (data, filename, titulo) => {
  if (!data?.length) return;
  const headers = Object.keys(data[0]);
  const rows = data.map(r => headers.map(h => r[h] ?? ''));
  const maxWidths = headers.map((h, i) => Math.max(h.length, ...rows.map(r => String(r[i] ?? '').length)) + 2);
  const xmlCols = maxWidths.map(w => `<Column ss:Width="${Math.min(w * 7, 200)}"/>`).join('');
  const headerRow = `<Row ss:StyleID="header">${headers.map(h => `<Cell><Data ss:Type="String">${h}</Data></Cell>`).join('')}</Row>`;
  const dataRows = rows.map(r => `<Row>${r.map(v => `<Cell><Data ss:Type="String">${String(v ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')}</Data></Cell>`).join('')}</Row>`).join('');
  const xml = `<?xml version="1.0" encoding="UTF-8"?><?mso-application progid="Excel.Sheet"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"><Styles><Style ss:ID="header"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#0a2540" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center"/></Style></Styles><Worksheet ss:Name="Reporte"><Table>${xmlCols}<Row><Cell ss:MergeAcross="${headers.length-1}"><Data ss:Type="String">${titulo}</Data></Cell></Row>${headerRow}${dataRows}</Table></Worksheet></Workbook>`;
  const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename + '.xls'; a.click();
  URL.revokeObjectURL(url);
};

const exportToPDF = (tableId, filename, titulo) => {
  const tableEl = document.getElementById(tableId);
  if (!tableEl) return;
  const printWindow = window.open('', '_blank');
  printWindow.document.write(`<html><head><title>${titulo}</title><style>body{font-family:Arial,sans-serif;font-size:12px;margin:20px}h2{color:#0a2540;margin-bottom:6px;font-size:16px}p{color:#6b7280;margin-bottom:14px;font-size:11px}table{width:100%;border-collapse:collapse}th{background:#0a2540;color:white;padding:7px 10px;text-align:left;font-size:11px}td{padding:5px 10px;border-bottom:1px solid #e5e7eb;font-size:11px}tr:nth-child(even){background:#f9fafb}</style></head><body><h2>${titulo}</h2><p>ProDominicana &nbsp;·&nbsp; Generado el ${new Date().toLocaleDateString('es-DO')}</p>${tableEl.outerHTML}</body></html>`);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { printWindow.print(); printWindow.close(); }, 500);
};

// ── Iconos SVG ───────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {typeof d === 'string' ? <path d={d}/> : d}
  </svg>
);
const IcoDashboard  = () => <Icon d={<><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></>} />;
const IcoPublicar   = () => <Icon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></>} />;
const IcoManual     = () => <Icon d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>} />;
const IcoSolicitudes= () => <Icon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></>} />;
const IcoReportes   = () => <Icon d={<><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>} />;
const IcoMiSolicitud= () => <Icon d={<><path d="M3 2h18v20H3z"/><path d="M8 6h8M8 10h8M8 14h5"/></>} />;
const IcoExcel      = () => <Icon size={14} d={<><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18"/></>} />;
const IcoPDF        = () => <Icon size={14} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>} />;
const IcoCSV        = () => <Icon size={14} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>} />;
const IcoEnviar     = () => <Icon size={14} d={<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>} />;
const IcoEditar     = () => <Icon size={14} d={<><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>} />;
const IcoContacto   = ({ size = 16 }) => <Icon size={size} d={<><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M15 8h2M15 12h2M7 16h10"/></>} />;
const IcoHistorial  = ({ size = 16 }) => <Icon size={size} d={<><path d="M3 3v5h5"/><path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/><path d="M12 7v5l4 2"/></>} />;
const IcoConfig     = ({ size = 16 }) => <Icon size={size} d={<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></>} />;
const IcoExcelUp    = ({ size = 16 }) => <Icon size={size} d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M12 18v-6M9 15l3-3 3 3"/></>} />;

const BtnExport = ({ onClick, icon, label, color }) => (
  <button onClick={onClick} style={{ display:'inline-flex', alignItems:'center', gap:'0.35rem', padding:'0.35rem 0.8rem', borderRadius:'0.4rem', border:`1.5px solid ${color}`, background:'white', color, fontWeight:'600', fontSize:'0.75rem', cursor:'pointer' }}>
    {icon}{label}
  </button>
);

// ── Estilos ──────────────────────────────────────────────────
const S = {
  card: { background:'white', borderRadius:'0.875rem', boxShadow:'0 2px 10px rgba(0,0,0,0.07)', border:'1px solid #e5e7eb', padding:'1.5rem', marginBottom:'1.25rem' },
  btn: (color='#2563eb', outline=false) => ({ padding:'0.5rem 1.25rem', borderRadius:'0.5rem', border: outline?`1.5px solid ${color}`:'none', background: outline?'transparent':color, color: outline?color:'white', fontWeight:'600', fontSize:'0.875rem', cursor:'pointer', display:'inline-flex', alignItems:'center', gap:'0.4rem', transition:'all .15s' }),
  badge: (color) => ({ padding:'0.2rem 0.65rem', borderRadius:'999px', fontSize:'0.75rem', fontWeight:'600', background:color+'20', color:color, display:'inline-block' }),
  input: { width:'100%', padding:'0.5rem 0.75rem', border:'1.5px solid #d1d5db', borderRadius:'0.5rem', fontSize:'0.875rem', boxSizing:'border-box' },
  label: { fontSize:'0.8rem', fontWeight:'600', color:'#374151', marginBottom:'0.3rem', display:'block' },
  table: { width:'100%', borderCollapse:'separate', borderSpacing:'0 2px', fontSize:'0.875rem' },
  th: { background:'#f3f4f6', padding:'0.6rem 0.85rem', textAlign:'left', fontWeight:'700', color:'#374151', borderBottom:'2px solid #e5e7eb', fontSize:'0.8rem', whiteSpace:'nowrap' },
  td: { padding:'0.6rem 0.85rem', borderBottom:'1px solid #f3f4f6', color:'#374151', verticalAlign:'middle' },
};

const TAB_ICONS = {
  dashboard:    <IcoDashboard />,
  publicar:     <IcoPublicar />,
  manual:       <IcoManual />,
  solicitudes:  <IcoSolicitudes />,
  reportes:     <IcoReportes />,
  miSolicitud:  <IcoMiSolicitud />,
};

// Estado inicial del form (extraído para reusar en reset)
const formInicial = () => ({
  semana: getSemanaActual(),
  año: new Date().getFullYear(),
  tipoMenu: 'Almuerzo',
  observaciones: '',
  combosPorDia: {
    Lunes:       [{ nombre:'Combo 1', descripcion:'' }, { nombre:'Combo 2', descripcion:'' }, { nombre:'Combo 3', descripcion:'' }],
    Martes:      [{ nombre:'Combo 1', descripcion:'' }, { nombre:'Combo 2', descripcion:'' }, { nombre:'Combo 3', descripcion:'' }],
    'Miércoles': [{ nombre:'Combo 1', descripcion:'' }, { nombre:'Combo 2', descripcion:'' }, { nombre:'Combo 3', descripcion:'' }],
    Jueves:      [{ nombre:'Combo 1', descripcion:'' }, { nombre:'Combo 2', descripcion:'' }, { nombre:'Combo 3', descripcion:'' }],
    Viernes:     [{ nombre:'Combo 1', descripcion:'' }, { nombre:'Combo 2', descripcion:'' }, { nombre:'Combo 3', descripcion:'' }],
  },
});

const DIAS_CORTOS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

const configInicial = () => ({
  recordatorioActivo: false, recordatorioDiaSemana: 1, recordatorioHora: 8,
  autoAperturaActivo: false, autoAperturaDiaSemana: 1, autoAperturaHora: 6,
  autoCierreActivo: false, autoCierreDiaSemana: 5, autoCierreHora: 12,
});

export default function AlmuerzosModule() {
  const { user } = useAuth();
  const rol = user?.rawRole?.toLowerCase() || '';
  const esRRHH = RRHH_ROLES.includes(rol);
  const empleadoID = user?.empleadoId || user?.empleadoID;
  const [tab, setTab] = useState(esRRHH ? 'dashboard' : 'miSolicitud');
  const [msg, setMsg] = useState(null);

  const tabs = [
    ...(esRRHH ? [
      { id:'dashboard',   label:'Dashboard'        },
      { id:'publicar',    label:'Publicar Menú'    },
      { id:'manual',      label:'Solicitud Manual' },
      { id:'solicitudes', label:'Solicitudes'      },
      { id:'reportes',    label:'Reportes'         },
    ] : []),
    { id:'miSolicitud', label:'Mi Solicitud' },
  ];

  const showMsg = (text, type='ok') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4500); };

  return (
    <div style={{ minHeight:'100vh', background:'#f8fafc', padding:'1.5rem' }}>
      <div style={{ background:'linear-gradient(135deg, #0a2540 0%, #1e5cb3 100%)', borderRadius:'1rem', padding:'1.5rem 2rem', marginBottom:'1.5rem', color:'white', display:'flex', alignItems:'center', gap:'1rem' }}>
        <IcoMiSolicitud />
        <div>
          <h1 style={{ margin:0, fontSize:'1.5rem', fontWeight:'700' }}>Solicitud de Almuerzo</h1>
          <p style={{ margin:0, opacity:.8, fontSize:'0.875rem' }}>Facilidades · ProDominicana · DTHS</p>
        </div>
      </div>

      {msg && (
        <div style={{ padding:'0.75rem 1.25rem', borderRadius:'0.5rem', marginBottom:'1rem', background: msg.type==='ok'?'#d1fae5':'#fee2e2', color: msg.type==='ok'?'#065f46':'#991b1b', fontWeight:'600', fontSize:'0.875rem' }}>{msg.text}</div>
      )}

      <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1.5rem', flexWrap:'wrap', background:'white', padding:'0.5rem', borderRadius:'0.75rem', boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding:'0.5rem 1rem', borderRadius:'0.5rem', border:'none', background: tab===t.id?'#2563eb':'transparent', color: tab===t.id?'white':'#6b7280', fontWeight: tab===t.id?'700':'500', cursor:'pointer', fontSize:'0.875rem', transition:'all .15s', display:'inline-flex', alignItems:'center', gap:'0.4rem' }}>
            {TAB_ICONS[t.id]}{t.label}
          </button>
        ))}
      </div>

      {tab === 'dashboard'   && <TabDashboard />}
      {tab === 'publicar'    && <TabPublicarMenu showMsg={showMsg} usuarioID={user?.id} />}
      {tab === 'manual'      && <TabSolicitudManual showMsg={showMsg} usuarioID={user?.id} />}
      {tab === 'solicitudes' && <TabSolicitudes showMsg={showMsg} esRRHH={esRRHH} />}
      {tab === 'reportes'    && <TabReportes />}
      {tab === 'miSolicitud' && <TabMiSolicitud showMsg={showMsg} empleadoID={empleadoID} esRRHH={esRRHH} />}
    </div>
  );
}

// ============================================================
// DASHBOARD
// ============================================================
// Combos solicitados de lunes a viernes (sábado y domingo muestran la próxima semana), con hoy resaltado
function CombosPorDia({ porComboSemana }) {
  const fechaISO = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  const now = new Date();
  const day = now.getDay();
  const lunes = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (day === 6 ? 2 : day === 0 ? 1 : 1 - day));
  const hoy = fechaISO(now);
  const dias = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'].map((nombre, i) => {
    const fecha = new Date(lunes.getFullYear(), lunes.getMonth(), lunes.getDate() + i);
    const iso = fechaISO(fecha);
    const combos = porComboSemana
      .filter(c => c.FechaAlmuerzo === iso)
      .sort((a, b) => a.NombreCombo.localeCompare(b.NombreCombo, 'es', { numeric: true }));
    return { nombre, iso, fecha, combos, total: combos.reduce((s, c) => s + c.cantidad, 0) };
  });
  const totalSemana = dias.reduce((s, d) => s + d.total, 0);
  const mes = (d) => d.toLocaleDateString('es-DO', { month: 'long' });
  const viernes = dias[4].fecha;
  const rango = mes(lunes) === mes(viernes)
    ? `${lunes.getDate()} al ${viernes.getDate()} de ${mes(viernes)}`
    : `${lunes.getDate()} de ${mes(lunes)} al ${viernes.getDate()} de ${mes(viernes)}`;

  return (
    <div style={{ ...S.card, padding:0, overflow:'hidden' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem', padding:'1.1rem 1.5rem', borderBottom:'1px solid #e5e7eb' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <span style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:'2.25rem', height:'2.25rem', borderRadius:'0.6rem', background:'#eff6ff', color:'#2563eb' }}>
            <IcoReportes />
          </span>
          <div>
            <h3 style={{ margin:0, fontWeight:'700', color:'#111827', fontSize:'1.05rem' }}>Combos Solicitados por Día</h3>
            <div style={{ fontSize:'0.8rem', color:'#6b7280', marginTop:'0.1rem' }}>Semana del {rango}</div>
          </div>
        </div>
        <div style={{ textAlign:'right' }}>
          <div style={{ fontSize:'1.5rem', fontWeight:'800', color:'#111827', lineHeight:1 }}>{totalSemana}</div>
          <div style={{ fontSize:'0.72rem', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em', marginTop:'0.2rem' }}>Solicitudes de la semana</div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:'1rem', padding:'1.25rem 1.5rem' }}>
        {dias.map(d => {
          const esHoy = d.iso === hoy;
          const pasado = d.iso < hoy;
          return (
            <div key={d.iso} style={{
              display:'flex', flexDirection:'column', borderRadius:'0.75rem', overflow:'hidden', background:'white',
              border: `1px solid ${esHoy ? '#93c5fd' : '#e5e7eb'}`,
              boxShadow: esHoy ? '0 4px 14px rgba(37,99,235,0.15)' : '0 1px 3px rgba(0,0,0,0.04)',
              opacity: pasado ? 0.7 : 1,
            }}>
              <div style={{ height:'4px', background: esHoy ? '#2563eb' : '#e5e7eb' }} />
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'0.75rem 1rem 0.6rem' }}>
                <div>
                  <div style={{ fontSize:'0.7rem', fontWeight:'700', letterSpacing:'0.08em', textTransform:'uppercase', color: esHoy ? '#2563eb' : '#6b7280' }}>{d.nombre}</div>
                  <div style={{ fontSize:'1.6rem', fontWeight:'800', color:'#111827', lineHeight:1.1 }}>{d.fecha.getDate()}</div>
                </div>
                {esHoy && (
                  <span style={{ background:'#2563eb', color:'white', fontSize:'0.65rem', fontWeight:'700', letterSpacing:'0.08em', padding:'0.2rem 0.55rem', borderRadius:'999px' }}>HOY</span>
                )}
              </div>

              <div style={{ flex:1, padding:'0 1rem 0.75rem' }}>
                {!d.combos.length
                  ? <div style={{ border:'1px dashed #d1d5db', borderRadius:'0.5rem', padding:'0.9rem 0.5rem', textAlign:'center', color:'#9ca3af', fontSize:'0.8rem' }}>Sin solicitudes</div>
                  : d.combos.map(c => (
                      <div key={c.NombreCombo} style={{ padding:'0.35rem 0' }}>
                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:'0.85rem', color:'#374151', marginBottom:'0.25rem' }}>
                          <span>{c.NombreCombo}</span>
                          <strong style={{ color:'#111827' }}>{c.cantidad}</strong>
                        </div>
                        <div style={{ height:'5px', borderRadius:'999px', background:'#f3f4f6', overflow:'hidden' }}>
                          <div style={{ width:`${(c.cantidad / d.total) * 100}%`, height:'100%', borderRadius:'999px', background: esHoy ? '#2563eb' : '#94a3b8' }} />
                        </div>
                      </div>
                    ))
                }
              </div>

              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.6rem 1rem', borderTop:'1px solid #f3f4f6', background: esHoy ? '#eff6ff' : '#f9fafb' }}>
                <span style={{ fontSize:'0.72rem', fontWeight:'600', color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.05em' }}>Total</span>
                <span style={{ fontSize:'1rem', fontWeight:'800', color: esHoy ? '#2563eb' : '#111827' }}>{d.total}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TabDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const cargar = useCallback(async () => {
    setLoading(true);
    try { setData(await almuerzosService.getDashboard()); } catch { setData(null); }
    setLoading(false);
  }, []);
  useEffect(() => { cargar(); const id = setInterval(cargar, 60000); return () => clearInterval(id); }, [cargar]);

  if (loading) return <p style={{ color:'#6b7280' }}>Cargando dashboard...</p>;
  if (!data)   return <p style={{ color:'#ef4444' }}>Error al cargar el dashboard.</p>;

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'1rem', marginBottom:'1.5rem' }}>
        {[
          { label:'Solicitudes Hoy', value:data.hoy,    color:'#2563eb' },
          { label:'Esta Semana',     value:data.semana, color:'#16a34a' },
          { label:'Este Mes',        value:data.mes,    color:'#dc143c' },
        ].map(c => (
          <div key={c.label} style={{ ...S.card, marginBottom:0, background:`linear-gradient(135deg, ${c.color} 0%, ${c.color}cc 100%)`, color:'white' }}>
            <div style={{ fontSize:'2.5rem', fontWeight:'800', lineHeight:1 }}>{c.value}</div>
            <div style={{ fontSize:'0.875rem', opacity:.9, marginTop:'0.25rem' }}>{c.label}</div>
          </div>
        ))}
      </div>
      <CombosPorDia porComboSemana={data.porComboSemana || []} />
      {data.menuActivo && (
        <div style={{ ...S.card, border:'2px solid #bbf7d0', background:'#f0fdf4' }}>
          <h3 style={{ margin:'0 0 0.5rem 0', color:'#15803d' }}>Menú Activo</h3>
          <p style={{ margin:0, color:'#166534', fontSize:'0.9rem' }}>
            <strong>Semana {data.menuActivo.Semana}/{data.menuActivo.Año}</strong> — {data.menuActivo.TipoMenu}<br />
            {fmtFecha(data.menuActivo.FechaInicio)} al {fmtFecha(data.menuActivo.FechaFin)}
            {data.menuActivo.EnviadoAlOferente
              ? <span style={{ marginLeft:'0.75rem', ...S.badge('#16a34a') }}><Check size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Enviado al oferente</span>
              : <span style={{ marginLeft:'0.75rem', ...S.badge('#f59e0b') }}>Pendiente de envío</span>
            }
          </p>
        </div>
      )}
    </div>
  );
}

// ============================================================
// PUBLICAR / EDITAR MENÚ
// ============================================================
function TabPublicarMenu({ showMsg, usuarioID }) {
  const [form, setForm]           = useState(formInicial());
  const [diaActivo, setDiaActivo] = useState('Lunes');
  const [menus, setMenus]         = useState([]);
  const [loading, setLoading]     = useState(false);
  // ── Estado de edición ──
  const [modoEdicion, setModoEdicion]       = useState(false);
  const [menuEditandoID, setMenuEditandoID] = useState(null);
  // ── Modal enviar ──
  const [modalEnviar, setModalEnviar]       = useState(null); // menu obj | null
  const [enviandoOferente, setEnviandoOferente] = useState(false);
  // ── Modal notificar colaboradores ──
  const [modalNotificar, setModalNotificar] = useState(null); // { menuID, semana, año } | null

  const [modalEliminar, setModalEliminar]   = useState(null); // menu obj | null
  const [fechaConfirmacion, setFechaConfirmacion] = useState('');
  const [errorEliminar, setErrorEliminar]   = useState('');
  const [eliminando, setEliminando]         = useState(false);
  const [enviandoNotificacion, setEnviandoNotificacion] = useState(false);
  // ── Datos del oferente (proveedor de almuerzo) ──
  const [oferente, setOferente]             = useState(null);
  const [modalOferente, setModalOferente]   = useState(false);
  const [oferenteForm, setOferenteForm]     = useState({ nombre:'', apellido:'', empresa:'', correo:'' });
  const [guardandoOferente, setGuardandoOferente] = useState(false);

  const cargarOferente = useCallback(async () => {
    try {
      const o = await almuerzosService.getOferente();
      setOferente(o);
      if (o) setOferenteForm({ nombre:o.Nombre||'', apellido:o.Apellido||'', empresa:o.Empresa||'', correo:o.Correo||'' });
    } catch { setOferente(null); }
  }, []);
  useEffect(() => { cargarOferente(); }, [cargarOferente]);

  // ── Historial de envíos al oferente ──
  const [modalHistorial, setModalHistorial] = useState(false);
  const [historial, setHistorial]           = useState([]);
  const [cargandoHistorial, setCargandoHistorial] = useState(false);
  const abrirHistorial = async () => {
    setModalHistorial(true);
    setCargandoHistorial(true);
    try { setHistorial(await almuerzosService.getHistorialOferente()); } catch { setHistorial([]); }
    setCargandoHistorial(false);
  };

  // ── Configuración: recordatorios y apertura/cierre automático ──
  const [modalConfig, setModalConfig]       = useState(false);
  const [config, setConfig]                 = useState(configInicial());
  const [guardandoConfig, setGuardandoConfig] = useState(false);
  const abrirConfig = async () => {
    setModalConfig(true);
    try {
      const c = await almuerzosService.getConfiguracion();
      if (c) setConfig({
        recordatorioActivo: !!c.RecordatorioActivo, recordatorioDiaSemana: c.RecordatorioDiaSemana ?? 1, recordatorioHora: c.RecordatorioHora ?? 8,
        autoAperturaActivo: !!c.AutoAperturaActivo, autoAperturaDiaSemana: c.AutoAperturaDiaSemana ?? 1, autoAperturaHora: c.AutoAperturaHora ?? 6,
        autoCierreActivo: !!c.AutoCierreActivo, autoCierreDiaSemana: c.AutoCierreDiaSemana ?? 5, autoCierreHora: c.AutoCierreHora ?? 12,
      });
    } catch { /* deja los valores por defecto */ }
  };
  const guardarConfig = async () => {
    setGuardandoConfig(true);
    try {
      await almuerzosService.guardarConfiguracion(config);
      showMsg('Configuración guardada');
      setModalConfig(false);
    } catch (e) { showMsg(e.response?.data?.error || 'Error al guardar la configuración', 'err'); }
    setGuardandoConfig(false);
  };

  // ── Carga del menú semanal desde Excel ──
  const [archivoExcel, setArchivoExcel]     = useState(null);
  const [cargandoExcel, setCargandoExcel]   = useState(false);
  const [errorExcel, setErrorExcel]         = useState('');
  const [inputExcelKey, setInputExcelKey]   = useState(0);
  const cargarDesdeExcel = async () => {
    if (!archivoExcel) return showMsg('Selecciona el archivo Excel', 'err');
    setCargandoExcel(true);
    setErrorExcel('');
    try {
      const resultado = await almuerzosService.cargarMenuDesdeExcel({
        semana: form.semana, año: form.año, tipoMenu: form.tipoMenu, observaciones: form.observaciones,
        fechaInicio: fmtD(lunes), fechaFin: fmtD(fin), creadoPor: usuarioID,
      }, archivoExcel);
      showMsg(resultado.message || 'Menú cargado exitosamente desde Excel');
      setArchivoExcel(null);
      setInputExcelKey(k => k + 1);
      setMenus(await almuerzosService.getMenus());
      if (resultado?.menuID) setModalNotificar({ menuID: resultado.menuID, semana: form.semana, año: form.año });
    } catch (e) {
      const texto = e.response?.data?.error || 'Error al cargar el Excel';
      setErrorExcel(texto);
      showMsg(texto, 'err');
    }
    setCargandoExcel(false);
  };
  const descargarPlantillaMenu = async () => {
    try {
      await almuerzosService.descargarPlantillaMenu({ fechaInicio: fmtD(lunes), tipoMenu: form.tipoMenu });
    } catch (e) { showMsg(e.response?.data?.error || 'Error al descargar la plantilla', 'err'); }
  };

  const guardarOferente = async () => {
    if (!oferenteForm.nombre.trim() || !oferenteForm.apellido.trim() || !oferenteForm.empresa.trim() || !oferenteForm.correo.trim()) {
      return showMsg('Nombre, apellido, empresa y correo son requeridos', 'err');
    }
    setGuardandoOferente(true);
    try {
      await almuerzosService.guardarOferente(oferenteForm);
      showMsg('Datos del oferente guardados');
      setModalOferente(false);
      await cargarOferente();
    } catch (e) { showMsg(e.response?.data?.error || 'Error al guardar el oferente', 'err'); }
    setGuardandoOferente(false);
  };

  // ── Cargar menús y calcular la próxima semana disponible ──
  useEffect(() => {
    almuerzosService.getMenus().then(data => {
      setMenus(data);
      // Calcular la próxima semana disponible
      const añoActual = new Date().getFullYear();
      const menusDelAño = data.filter(m => m.Año === añoActual && m.TipoMenu === 'Almuerzo');
      if (menusDelAño.length > 0) {
        const semanaMaxima = Math.max(...menusDelAño.map(m => m.Semana));
        const proximaSemana = semanaMaxima + 1;
        if (proximaSemana <= 53) {
          setForm(f => ({ ...f, semana: proximaSemana }));
        }
      }
    }).catch(() => {});
  }, []);

  const tipoConfig = TIPOS_MENU.find(t => t.key === form.tipoMenu);
  const lunes = getLunesDeSemana(form.semana, form.año);
  const fin   = new Date(lunes); fin.setDate(fin.getDate() + (form.tipoMenu === 'Cena' ? 6 : 4));
  const diasDisponibles = tipoConfig?.dias || DIAS_SEMANA;
  const diaActivoReal = diasDisponibles.includes(diaActivo) ? diaActivo : diasDisponibles[0];

  const getCombos = (dia) => form.combosPorDia[dia] || [];
  const setCombos = (dia, combos) => setForm(f => ({ ...f, combosPorDia: { ...f.combosPorDia, [dia]: combos } }));
  const agrCombo  = (dia) => setCombos(dia, [...getCombos(dia), { nombre:`Combo ${getCombos(dia).length + 1}`, descripcion:'' }]);
  const rmCombo   = (dia, i) => setCombos(dia, getCombos(dia).filter((_, idx) => idx !== i));
  const setCombo  = (dia, i, field, val) => { const arr = [...getCombos(dia)]; arr[i] = { ...arr[i], [field]: val }; setCombos(dia, arr); };
  const copiarATodos = (dia) => {
    const source = getCombos(dia);
    const nuevo = {};
    diasDisponibles.forEach(d => { nuevo[d] = source.map(c => ({ ...c })); });
    setForm(f => ({ ...f, combosPorDia: { ...f.combosPorDia, ...nuevo } }));
    showMsg(`Combos del ${dia} copiados a todos los días`);
  };

  // ── Cargar menú existente para editar ───────────────────────
  const cargarMenuParaEditar = async (menu) => {
    try {
      const cs = await almuerzosService.getCombosDeMenu(menu.MenuID);
      const tipoConf = TIPOS_MENU.find(t => t.key === menu.TipoMenu);
      const diasMenu = tipoConf?.dias || DIAS_SEMANA;

      const combosPorDia = {};
      diasMenu.forEach(d => { combosPorDia[d] = []; });
      cs.forEach(c => {
        const dia = c.DiaSemana?.trim();
        if (combosPorDia[dia] !== undefined) {
          combosPorDia[dia].push({ nombre: c.NombreCombo, descripcion: c.Descripcion || '' });
        }
      });
      // Asegurar mínimo un combo por día si quedó vacío
      diasMenu.forEach(d => {
        if (!combosPorDia[d].length) combosPorDia[d] = [{ nombre:'Combo 1', descripcion:'' }];
      });

      setForm({ semana: menu.Semana, año: menu.Año, tipoMenu: menu.TipoMenu, observaciones: menu.Observaciones || '', combosPorDia });
      setDiaActivo(diasMenu[0]);
      setModoEdicion(true);
      setMenuEditandoID(menu.MenuID);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      showMsg(`Editando menú Semana ${menu.Semana}/${menu.Año}`);
    } catch {
      showMsg('Error al cargar el menú para editar', 'err');
    }
  };

  const cancelarEdicion = () => {
    setModoEdicion(false);
    setMenuEditandoID(null);
    setForm(formInicial());
    setDiaActivo('Lunes');
  };

  // ── Publicar / Guardar cambios ───────────────────────────────
  const publicar = async () => {
    const combosFlat = [];
    for (const dia of diasDisponibles) {
      const cs = getCombos(dia);
      if (!cs.length) return showMsg(`Agrega al menos un combo para ${dia}`, 'err');
      if (cs.some(c => !c.nombre.trim())) return showMsg(`Todos los combos del ${dia} deben tener nombre`, 'err');
      cs.forEach((c, i) => combosFlat.push({ nombre: c.nombre, descripcion: c.descripcion, diaSemana: dia, orden: i + 1 }));
    }
    setLoading(true);
    try {
      if (modoEdicion && menuEditandoID) {
        await almuerzosService.actualizarMenu(menuEditandoID, { combos: combosFlat, observaciones: form.observaciones });
        showMsg('Menú actualizado exitosamente');
        cancelarEdicion();
      } else {
        const resultado = await almuerzosService.crearMenu({
          semana: form.semana, año: form.año,
          fechaInicio: fmtD(lunes), fechaFin: fmtD(fin),
          tipoMenu: form.tipoMenu, observaciones: form.observaciones,
          combos: combosFlat, creadoPor: usuarioID,
        });
        showMsg('Menú creado exitosamente');
        // Mostrar modal para notificar a colaboradores
        if (resultado?.menuID) {
          setModalNotificar({ menuID: resultado.menuID, semana: form.semana, año: form.año });
        }
      }
      setMenus(await almuerzosService.getMenus());
    } catch (e) { showMsg(e.response?.data?.error || 'Error al guardar', 'err'); }
    setLoading(false);
  };

  // ── Enviar notificación a todos los colaboradores ───────────────
  const enviarNotificacionATodos = async () => {
    if (!modalNotificar) return;
    setEnviandoNotificacion(true);
    try {
      const resultado = await almuerzosService.publicarMenuConNotificacion(modalNotificar.menuID, true);
      if (resultado.notificacionEnviada) {
        const detalle = resultado.enviados != null
          ? ` (${resultado.enviados} colaboradores${resultado.fallidos ? `, ${resultado.fallidos} fallidos` : ''}${resultado.sinCorreo ? `, ${resultado.sinCorreo} sin correo registrado` : ''})`
          : '';
        showMsg(`Notificación enviada${detalle}`);
      } else {
        showMsg(`Menú publicado pero hubo un error al enviar notificación: ${resultado.notificacionError || 'Error desconocido'}`, 'err');
      }
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al enviar notificación', 'err');
    }
    setEnviandoNotificacion(false);
    setModalNotificar(null);
  };

  // ── Notificar desde la lista de menús ───────────────────────────
  const notificarMenu = async (menu) => {
    setModalNotificar({ menuID: menu.MenuID, semana: menu.Semana, año: menu.Año });
  };

  const cerrarMenu = async (id) => {
    if (!window.confirm('¿Cerrar este menú? Ya no se podrán hacer solicitudes. Si lo publicaste por error, después de cerrarlo podrás eliminarlo.')) return;
    await almuerzosService.cambiarEstadoMenu(id, 'Cerrado');
    showMsg('Menú cerrado');
    setMenus(await almuerzosService.getMenus());
  };

  // ── Eliminar menú (solo cerrado, confirmando la fecha de inicio) ──
  const abrirEliminar = (menu) => { setModalEliminar(menu); setFechaConfirmacion(''); setErrorEliminar(''); };
  const confirmarEliminar = async () => {
    if (!modalEliminar) return;
    setEliminando(true);
    setErrorEliminar('');
    try {
      const r = await almuerzosService.eliminarMenu(modalEliminar.MenuID, fechaConfirmacion);
      showMsg(r.message);
      setModalEliminar(null);
      setMenus(await almuerzosService.getMenus());
    } catch (e) {
      setErrorEliminar(e.response?.data?.error || 'Error al eliminar el menú');
    }
    setEliminando(false);
  };

  // ── Enviar al oferente ───────────────────────────────────────
  const confirmarEnvio = async () => {
    if (!modalEnviar) return;
    setEnviandoOferente(true);
    try {
      const resultado = await almuerzosService.enviarAlOferente(modalEnviar.MenuID);
      showMsg(resultado.message);
      setMenus(await almuerzosService.getMenus());
      setModalEnviar(null);
    } catch (e) {
      showMsg(e.response?.data?.error || 'Error al enviar al oferente', 'err');
    }
    setEnviandoOferente(false);
  };

  return (
    <div>
      {/* ── Modal de confirmación de envío ── */}
      {modalEnviar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'420px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 0.75rem 0', color:'#1f2937' }}> Enviar al Oferente</h3>
            <p style={{ color:'#374151', margin:'0 0 0.5rem 0' }}>
              ¿Confirmas el envío del menú <strong>Semana {modalEnviar.Semana}/{modalEnviar.Año}</strong>?
            </p>
            <div style={{ background:'#eff6ff', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'0.75rem', fontSize:'0.875rem', color:'#1d4ed8' }}>
              <strong>{modalEnviar.TotalSolicitudes}</strong> solicitudes confirmadas serán enviadas al proveedor.<br/>
              Período: {fmtFecha(modalEnviar.FechaInicio)} al {fmtFecha(modalEnviar.FechaFin)}
            </div>
            {oferente ? (
              <div style={{ background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1.25rem', fontSize:'0.85rem', color:'#334155' }}>
                Se enviará por correo a:<br/>
                <strong>{oferente.Nombre} {oferente.Apellido}</strong> — {oferente.Empresa}<br/>
                {oferente.Correo}
              </div>
            ) : (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1.25rem', fontSize:'0.85rem', color:'#b91c1c' }}>
                No hay un oferente registrado todavía.{' '}
                <button onClick={() => { setModalEnviar(null); setModalOferente(true); }} style={{ background:'none', border:'none', color:'#b91c1c', textDecoration:'underline', cursor:'pointer', padding:0, fontSize:'0.85rem' }}>
                  Registrarlo ahora
                </button>
              </div>
            )}
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
              <button onClick={() => setModalEnviar(null)} disabled={enviandoOferente} style={S.btn('#6b7280', true)}>Cancelar</button>
              <button onClick={confirmarEnvio} disabled={enviandoOferente || !oferente} style={S.btn('#16a34a')}>
                <IcoEnviar /> {enviandoOferente ? 'Enviando...' : 'Confirmar Envío'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de eliminación de menú ── */}
      {modalEliminar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'460px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 0.75rem 0', color:'#b91c1c' }}>Eliminar menú</h3>
            <p style={{ color:'#374151', margin:'0 0 0.75rem 0' }}>
              Vas a eliminar el menú <strong>{modalEliminar.TipoMenu} · Semana {modalEliminar.Semana}/{modalEliminar.Año}</strong>,
              del <strong>{fmtFecha(modalEliminar.FechaInicio)}</strong> al <strong>{fmtFecha(modalEliminar.FechaFin)}</strong>. Esta acción no se puede deshacer.
            </p>
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1rem', fontSize:'0.85rem', color:'#991b1b' }}>
              Se borrarán también sus combos{modalEliminar.TotalSolicitudes > 0 && <> y las <strong>{modalEliminar.TotalSolicitudes} solicitudes de almuerzo confirmadas</strong> de los colaboradores</>}.
              {!!modalEliminar.EnviadoAlOferente && <><br/>Este menú ya fue enviado al oferente: avísale que quedó sin efecto.</>}
            </div>
            <label style={S.label}>Para confirmar, indica la fecha de inicio del menú (lunes {fmtFecha(modalEliminar.FechaInicio)})</label>
            <input type="date" style={{ ...S.input, marginBottom:'0.75rem' }} value={fechaConfirmacion} onChange={e => { setFechaConfirmacion(e.target.value); setErrorEliminar(''); }} />
            {fechaConfirmacion && fechaConfirmacion !== modalEliminar.FechaInicio && (
              <p style={{ margin:'0 0 0.75rem 0', color:'#b91c1c', fontSize:'0.8rem' }}>
                La fecha no corresponde a este menú (empieza el {fmtFecha(modalEliminar.FechaInicio)}).
              </p>
            )}
            {errorEliminar && <p style={{ margin:'0 0 0.75rem 0', color:'#b91c1c', fontSize:'0.85rem', fontWeight:'600' }}>{errorEliminar}</p>}
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
              <button onClick={() => setModalEliminar(null)} disabled={eliminando} style={S.btn('#6b7280', true)}>Cancelar</button>
              <button onClick={confirmarEliminar} disabled={eliminando || fechaConfirmacion !== modalEliminar.FechaInicio} style={{ ...S.btn('#dc2626'), opacity: (eliminando || fechaConfirmacion !== modalEliminar.FechaInicio) ? .5 : 1 }}>
                {eliminando ? 'Eliminando...' : 'Eliminar menú'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de datos del oferente ── */}
      {modalOferente && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'420px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 1rem 0', color:'#1f2937', display:'flex', alignItems:'center', gap:'0.5rem' }}><IcoContacto /> Datos del Oferente</h3>
            <div style={{ display:'grid', gap:'0.75rem', marginBottom:'1.25rem' }}>
              <div>
                <label style={S.label}>Nombre</label>
                <input style={S.input} value={oferenteForm.nombre} onChange={e => setOferenteForm(f => ({ ...f, nombre:e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Apellido</label>
                <input style={S.input} value={oferenteForm.apellido} onChange={e => setOferenteForm(f => ({ ...f, apellido:e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Empresa</label>
                <input style={S.input} value={oferenteForm.empresa} onChange={e => setOferenteForm(f => ({ ...f, empresa:e.target.value }))} />
              </div>
              <div>
                <label style={S.label}>Correo</label>
                <input type="email" style={S.input} value={oferenteForm.correo} onChange={e => setOferenteForm(f => ({ ...f, correo:e.target.value }))} />
              </div>
            </div>
            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
              <button onClick={() => setModalOferente(false)} disabled={guardandoOferente} style={S.btn('#6b7280', true)}>Cancelar</button>
              <button onClick={guardarOferente} disabled={guardandoOferente} style={S.btn('#2563eb')}>
                {guardandoOferente ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de historial de envíos al oferente ── */}
      {modalHistorial && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'700px', width:'92%', maxHeight:'80vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
              <h3 style={{ margin:0, color:'#1f2937', display:'flex', alignItems:'center', gap:'0.5rem' }}><IcoHistorial /> Historial de Envíos al Oferente</h3>
              <button onClick={() => setModalHistorial(false)} style={{ ...S.btn('#6b7280', true), padding:'0.35rem 0.75rem' }} aria-label="Cerrar"><X size={16} /></button>
            </div>
            {cargandoHistorial ? <p style={{ color:'#6b7280' }}>Cargando...</p> : !historial.length ? (
              <p style={{ color:'#6b7280' }}>Aún no se ha enviado ningún menú al oferente.</p>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={S.table}>
                  <thead>
                    <tr>{['Fecha','Semana','Oferente','Solicitudes','Resultado','Enviado por'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {historial.map(h => (
                      <tr key={h.EnvioID}>
                        <td style={S.td}>{new Date(h.FechaEnvio).toLocaleString('es-DO')}</td>
                        <td style={S.td}>S{h.Semana}/{h.Año}</td>
                        <td style={S.td}>{h.OferenteNombre} {h.OferenteApellido} — {h.OferenteEmpresa}</td>
                        <td style={S.td}>{h.TotalSolicitudes}</td>
                        <td style={S.td}>
                          {h.Exito
                            ? <span style={S.badge('#16a34a')}>Enviado</span>
                            : <span style={S.badge('#ef4444')} title={h.Error || ''}>Error</span>
                          }
                        </td>
                        <td style={S.td}>{h.EnviadoPorNombre || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal de configuración: recordatorios y apertura/cierre automático ── */}
      {modalConfig && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'520px', width:'92%', maxHeight:'85vh', overflowY:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.2)' }}>
            <h3 style={{ margin:'0 0 0.35rem 0', color:'#1f2937', display:'flex', alignItems:'center', gap:'0.5rem' }}><IcoConfig /> Configuración de Almuerzos</h3>
            <p style={{ margin:'0 0 1.25rem 0', color:'#6b7280', fontSize:'0.85rem' }}>Todo es opcional — si un interruptor está apagado, ese comportamiento sigue siendo manual como hoy.</p>

            {[
              {
                key: 'recordatorio', titulo: 'Recordatorio a DTHS',
                desc: 'Avisa (campanita + correo) si falta publicar el menú de la semana o enviarlo al oferente.',
                activo: 'recordatorioActivo', dia: 'recordatorioDiaSemana', hora: 'recordatorioHora',
              },
              {
                key: 'apertura', titulo: 'Apertura automática del menú',
                desc: 'Si hay un menú en Borrador para la semana, lo publica automáticamente en el día/hora indicados.',
                activo: 'autoAperturaActivo', dia: 'autoAperturaDiaSemana', hora: 'autoAperturaHora',
              },
              {
                key: 'cierre', titulo: 'Cierre automático del menú',
                desc: 'Cierra automáticamente el menú Activo de la semana en el día/hora indicados.',
                activo: 'autoCierreActivo', dia: 'autoCierreDiaSemana', hora: 'autoCierreHora',
              },
            ].map(sec => (
              <div key={sec.key} style={{ border:'1px solid #e5e7eb', borderRadius:'0.75rem', padding:'1rem', marginBottom:'1rem' }}>
                <label style={{ display:'flex', alignItems:'center', gap:'0.6rem', cursor:'pointer', marginBottom:'0.5rem' }}>
                  <input type="checkbox" checked={config[sec.activo]} onChange={e => setConfig(c => ({ ...c, [sec.activo]: e.target.checked }))} />
                  <span style={{ fontWeight:'700', color:'#1f2937', fontSize:'0.9rem' }}>{sec.titulo}</span>
                </label>
                <p style={{ margin:'0 0 0.75rem 1.9rem', color:'#6b7280', fontSize:'0.8rem' }}>{sec.desc}</p>
                {config[sec.activo] && (
                  <div style={{ display:'flex', gap:'0.75rem', marginLeft:'1.9rem' }}>
                    <div>
                      <label style={S.label}>Día</label>
                      <select style={S.input} value={config[sec.dia]} onChange={e => setConfig(c => ({ ...c, [sec.dia]: parseInt(e.target.value) }))}>
                        {DIAS_CORTOS.map((d, i) => <option key={i} value={i}>{d}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Hora</label>
                      <select style={S.input} value={config[sec.hora]} onChange={e => setConfig(c => ({ ...c, [sec.hora]: parseInt(e.target.value) }))}>
                        {Array.from({ length:24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            ))}

            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end' }}>
              <button onClick={() => setModalConfig(false)} disabled={guardandoConfig} style={S.btn('#6b7280', true)}>Cancelar</button>
              <button onClick={guardarConfig} disabled={guardandoConfig} style={S.btn('#2563eb')}>
                {guardandoConfig ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal de notificación a colaboradores ── */}
      {modalNotificar && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:'white', borderRadius:'1rem', padding:'2rem', maxWidth:'480px', width:'90%', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ textAlign:'center', marginBottom:'1.5rem' }}>
              <div style={{ marginBottom:'0.5rem', color:'#2563eb' }}><Mail size={48} /></div>
              <h3 style={{ margin:'0 0 0.5rem 0', color:'#1f2937', fontSize:'1.25rem' }}>Notificar a Colaboradores</h3>
              <p style={{ color:'#6b7280', margin:0, fontSize:'0.95rem' }}>
                El menú <strong>Semana {modalNotificar.semana}/{modalNotificar.año}</strong> ha sido creado exitosamente.
              </p>
            </div>

            <div style={{ background:'#ecfdf5', border:'1px solid #a7f3d0', borderRadius:'0.75rem', padding:'1rem', marginBottom:'1.5rem' }}>
              <p style={{ margin:0, color:'#065f46', fontSize:'0.9rem', lineHeight:'1.5' }}>
                <strong>¿Desea enviar una notificación por correo a todos los colaboradores?</strong><br/>
                Se les informará que el menú está disponible para que realicen su selección de almuerzo.
              </p>
            </div>

            <div style={{ display:'flex', gap:'0.75rem', justifyContent:'center' }}>
              <button
                onClick={() => setModalNotificar(null)}
                disabled={enviandoNotificacion}
                style={{ ...S.btn('#6b7280', true), minWidth:'120px' }}
              >
                No, omitir
              </button>
              <button
                onClick={enviarNotificacionATodos}
                disabled={enviandoNotificacion}
                style={{ ...S.btn('#2563eb'), minWidth:'180px', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}
              >
                {enviandoNotificacion ? (
                  <>
                    <span style={{ width:'16px', height:'16px', border:'2px solid #fff', borderTopColor:'transparent', borderRadius:'50%', animation:'spin 1s linear infinite' }}></span>
                    Enviando...
                  </>
                ) : (
                  <><Send size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Sí, notificar a todos</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Formulario Publicar / Editar ── */}
      <div style={{ ...S.card, border: modoEdicion ? '2px solid #f59e0b' : '1px solid #e5e7eb', background: modoEdicion ? '#fffbeb' : 'white' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem', flexWrap:'wrap', gap:'0.5rem' }}>
          <h3 style={{ margin:0, fontWeight:'700', color: modoEdicion ? '#92400e' : '#1f2937' }}>
            {modoEdicion ? <><Pencil size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Editando Menú — Semana {form.semana}/{form.año}</> : 'Publicar Nuevo Menú Semanal'}
          </h3>
          {modoEdicion && (
            <button onClick={cancelarEdicion} style={{ ...S.btn('#6b7280', true), fontSize:'0.8rem', padding:'0.35rem 0.85rem' }}>
              <X size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Cancelar edición
            </button>
          )}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:'1rem', marginBottom:'1rem' }}>
          <div>
            <label style={S.label}>Tipo de Menú</label>
            <select style={{ ...S.input, opacity: modoEdicion ? .6 : 1 }} disabled={modoEdicion} value={form.tipoMenu} onChange={e => setForm(f => ({ ...f, tipoMenu: e.target.value }))}>
              {TIPOS_MENU.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label style={S.label}>Semana #</label>
            <input type="number" style={{ ...S.input, opacity: modoEdicion ? .6 : 1 }} disabled={modoEdicion} min={1} max={53} value={form.semana} onChange={e => setForm(f => ({ ...f, semana: parseInt(e.target.value) }))} />
          </div>
          <div>
            <label style={S.label}>Año</label>
            <input type="number" style={{ ...S.input, opacity: modoEdicion ? .6 : 1 }} disabled={modoEdicion} value={form.año} onChange={e => setForm(f => ({ ...f, año: parseInt(e.target.value) }))} />
          </div>
        </div>

        <div style={{ background:'#eff6ff', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1.25rem', fontSize:'0.875rem', color:'#1d4ed8', fontWeight:'600' }}>
          Período: {fmtD(lunes)} al {fmtD(fin)} — Días: {diasDisponibles.join(', ')}
        </div>

        {/* Tabs por día */}
        <div style={{ marginBottom:'1rem' }}>
          <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap', borderBottom:'2px solid #e5e7eb' }}>
            {diasDisponibles.map(dia => {
              const combos = getCombos(dia);
              const tieneContenido = combos.some(c => c.descripcion.trim());
              return (
                <button key={dia} onClick={() => setDiaActivo(dia)} style={{ padding:'0.5rem 1.1rem', border:'none', borderBottom: diaActivoReal===dia?'2px solid #2563eb':'2px solid transparent', marginBottom:'-2px', background:'transparent', color: diaActivoReal===dia?'#2563eb':'#6b7280', fontWeight: diaActivoReal===dia?'700':'500', cursor:'pointer', fontSize:'0.85rem' }}>
                  {dia}
                  {tieneContenido && <span style={{ marginLeft:'0.3rem', ...S.badge('#16a34a'), fontSize:'0.6rem', padding:'0.05rem 0.35rem' }}><Check size={10} /></span>}
                  <span style={{ marginLeft:'0.3rem', ...S.badge('#6b7280'), fontSize:'0.6rem', padding:'0.05rem 0.35rem' }}>{combos.length}</span>
                </button>
              );
            })}
          </div>
          <div style={{ border:'1px solid #e5e7eb', borderTop:'none', borderRadius:'0 0 0.75rem 0.75rem', padding:'1rem', background:'#fafafa' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.75rem' }}>
              <label style={{ ...S.label, margin:0 }}>Combos para el <strong>{diaActivoReal}</strong></label>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={() => copiarATodos(diaActivoReal)} style={{ ...S.btn('#f59e0b'), fontSize:'0.75rem', padding:'0.35rem 0.75rem' }}>Copiar a todos</button>
                <button onClick={() => agrCombo(diaActivoReal)} style={{ ...S.btn('#16a34a'), fontSize:'0.75rem', padding:'0.35rem 0.75rem' }}>+ Combo</button>
              </div>
            </div>
            {getCombos(diaActivoReal).map((c, i) => (
              <div key={i} style={{ display:'grid', gridTemplateColumns:'160px 1fr 36px', gap:'0.5rem', marginBottom:'0.5rem', alignItems:'center' }}>
                <input placeholder={`Nombre (ej: Combo ${i+1})`} style={{ ...S.input, background:'white' }} value={c.nombre} onChange={e => setCombo(diaActivoReal, i, 'nombre', e.target.value)} />
                <input placeholder="Descripción del plato..." style={{ ...S.input, background:'white' }} value={c.descripcion} onChange={e => setCombo(diaActivoReal, i, 'descripcion', e.target.value)} />
                <button onClick={() => rmCombo(diaActivoReal, i)} style={{ ...S.btn('#ef4444'), padding:'0.45rem', justifyContent:'center' }} aria-label="Quitar combo"><X size={16} /></button>
              </div>
            ))}
            {!getCombos(diaActivoReal).length && <p style={{ color:'#9ca3af', fontSize:'0.85rem', margin:0 }}>No hay combos. Haz clic en "+ Combo".</p>}
          </div>
        </div>

        <div style={{ marginBottom:'1rem' }}>
          <label style={S.label}>Observaciones generales</label>
          <textarea style={{ ...S.input, height:'60px', resize:'vertical' }} value={form.observaciones} onChange={e => setForm(f => ({ ...f, observaciones: e.target.value }))} />
        </div>

        <div style={{ display:'flex', gap:'0.75rem' }}>
          <button onClick={publicar} disabled={loading} style={S.btn(modoEdicion ? '#f59e0b' : '#2563eb')}>
            {loading ? 'Guardando...' : modoEdicion ? ' Guardar Cambios' : ' Publicar Menú'}
          </button>
          {modoEdicion && (
            <button onClick={cancelarEdicion} style={S.btn('#6b7280', true)}>Cancelar</button>
          )}
        </div>

        {!modoEdicion && (
          <div style={{ marginTop:'1.5rem', paddingTop:'1.25rem', borderTop:'1px dashed #d1d5db' }}>
            <h4 style={{ margin:'0 0 0.35rem 0', fontWeight:'700', color:'#374151', fontSize:'0.9rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <IcoExcelUp size={15} /> O cargar el menú desde Excel
            </h4>
            <p style={{ margin:'0 0 0.75rem 0', color:'#6b7280', fontSize:'0.8rem' }}>
              Usa la semana, año, tipo de menú y observaciones definidos arriba. Descarga la plantilla (formato MENU PRO-DOMINICANA, ya trae las fechas de la semana {fmtFecha(fmtD(lunes))} al {fmtFecha(fmtD(fin))}), complétala con el plato de cada combo por día y súbela aquí.
              La fecha del archivo (fila 3, formato dd/mm/aaaa - dd/mm/aaaa) debe coincidir con la semana seleccionada; si no coincide, el archivo se rechaza.
            </p>
            <div style={{ display:'flex', gap:'0.75rem', alignItems:'center', flexWrap:'wrap' }}>
              <button onClick={descargarPlantillaMenu} style={{ ...S.btn('#6b7280', true), fontSize:'0.8rem', padding:'0.4rem 0.85rem' }}>
                Descargar plantilla
              </button>
              <input
                key={inputExcelKey}
                type="file"
                accept=".xlsx"
                onChange={e => { setArchivoExcel(e.target.files?.[0] || null); setErrorExcel(''); }}
                style={{ fontSize:'0.8rem' }}
              />
              <button onClick={cargarDesdeExcel} disabled={cargandoExcel || !archivoExcel} style={{ ...S.btn('#16a34a'), fontSize:'0.8rem', padding:'0.4rem 0.85rem' }}>
                {cargandoExcel ? 'Cargando...' : 'Cargar Excel'}
              </button>
            </div>
            {errorExcel && (
              <div style={{ marginTop:'0.75rem', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'0.5rem', padding:'0.65rem 0.9rem', color:'#b91c1c', fontSize:'0.82rem' }}>
                <strong>No se cargó el menú:</strong> {errorExcel}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Menús publicados ── */}
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
          <h3 style={{ margin:0, fontWeight:'700', color:'#1f2937' }}>Menús Publicados</h3>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
            <button onClick={() => setModalOferente(true)} style={{ ...S.btn(oferente ? '#6b7280' : '#f59e0b', true), fontSize:'0.8rem', padding:'0.4rem 0.85rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <IcoContacto size={14} /> {oferente ? `Oferente: ${oferente.Nombre} ${oferente.Apellido}` : 'Registrar Oferente'}
            </button>
            <button onClick={abrirHistorial} style={{ ...S.btn('#6b7280', true), fontSize:'0.8rem', padding:'0.4rem 0.85rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <IcoHistorial size={14} /> Historial de Envíos
            </button>
            <button onClick={abrirConfig} style={{ ...S.btn('#6b7280', true), fontSize:'0.8rem', padding:'0.4rem 0.85rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
              <IcoConfig size={14} /> Configuración
            </button>
          </div>
        </div>
        {!menus.length ? <p style={{ color:'#6b7280' }}>No hay menús publicados.</p> : (
          <div style={{ overflowX:'auto' }}>
            <table style={S.table}>
              <thead>
                <tr>{['Semana','Tipo','Desde','Hasta','Estado','Solicitudes','Enviado','Acciones'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {menus.map(m => (
                  <tr key={m.MenuID} style={{ background: menuEditandoID === m.MenuID ? '#fffbeb' : 'white' }}>
                    <td style={S.td}><strong>S{m.Semana}/{m.Año}</strong></td>
                    <td style={S.td}>{m.TipoMenu}</td>
                    <td style={S.td}>{fmtFecha(m.FechaInicio)}</td>
                    <td style={S.td}>{fmtFecha(m.FechaFin)}</td>
                    <td style={S.td}><span style={S.badge(m.Estado==='Activo'?'#16a34a':m.Estado==='Cerrado'?'#6b7280':'#f59e0b')}>{m.Estado}</span></td>
                    <td style={S.td}><strong>{m.TotalSolicitudes}</strong></td>
                    <td style={S.td}>
                      {m.EnviadoAlOferente
                        ? <span style={S.badge('#16a34a')}><Check size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />{m.FechaEnvio ? new Date(m.FechaEnvio).toLocaleDateString('es-DO') : 'Enviado'}</span>
                        : <span style={S.badge('#9ca3af')}>Pendiente</span>
                      }
                    </td>
                    <td style={S.td}>
                      <div style={{ display:'flex', gap:'0.35rem', flexWrap:'wrap' }}>
                        {m.Estado === 'Activo' && (
                          <>
                            <button
                              onClick={() => cargarMenuParaEditar(m)}
                              style={{ ...S.btn('#2563eb', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem', gap:'0.25rem' }}
                            >
                              <IcoEditar /> Editar
                            </button>
                            <button
                              onClick={() => notificarMenu(m)}
                              style={{ ...S.btn('#8b5cf6', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem', gap:'0.25rem' }}
                              title="Enviar notificación a todos los colaboradores"
                            >
                              <Mail size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Notificar
                            </button>
                            {!m.EnviadoAlOferente && (
                              <button
                                onClick={() => setModalEnviar(m)}
                                style={{ ...S.btn('#16a34a', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem', gap:'0.25rem' }}
                              >
                                <IcoEnviar /> Enviar
                              </button>
                            )}
                            <button
                              onClick={() => cerrarMenu(m.MenuID)}
                              style={{ ...S.btn('#6b7280', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem' }}
                            >
                              Cerrar
                            </button>
                          </>
                        )}
                        {m.Estado === 'Cerrado' && !m.EnviadoAlOferente && (
                          <button
                            onClick={() => setModalEnviar(m)}
                            style={{ ...S.btn('#16a34a', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem', gap:'0.25rem' }}
                          >
                            <IcoEnviar /> Enviar
                          </button>
                        )}
                        {m.Estado === 'Cerrado' && (
                          <button
                            onClick={() => abrirEliminar(m)}
                            style={{ ...S.btn('#dc2626', true), fontSize:'0.72rem', padding:'0.28rem 0.65rem' }}
                            title="Eliminar este menú (solo menús cerrados)"
                          >
                            Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// SOLICITUD MANUAL
// ============================================================
function TabSolicitudManual({ showMsg, usuarioID }) {
  const [empleados, setEmpleados] = useState([]);
  const [menus, setMenus]         = useState([]);
  const [menuSel, setMenuSel]     = useState(null);
  const [combos, setCombos]       = useState([]);
  const [form, setForm]           = useState({ empleadoID:'', menuID:'', tipoComida:'Almuerzo', combo:{} });
  const [loading, setLoading]     = useState(false);
  const [modo, setModo]           = useState('individual'); // 'individual' | 'masiva'

  useEffect(() => {
    almuerzosService.getEmpleados().then(setEmpleados).catch(() => {});
    almuerzosService.getMenus({ estado:'Activo' }).then(setMenus).catch(() => {});
  }, []);

  const onMenuChange = async menuID => {
    setForm(f => ({ ...f, menuID, combo:{} }));
    if (!menuID) { setMenuSel(null); setCombos([]); return; }
    const m = menus.find(x => x.MenuID == menuID);
    setMenuSel(m || null);
    setCombos(await almuerzosService.getCombosDeMenu(menuID));
  };

  const tipoConfig = TIPOS_MENU.find(t => t.key === form.tipoComida);
  const dias = menuSel ? calcDias(menuSel.FechaInicio, tipoConfig?.dias || DIAS_SEMANA) : [];
  const combosPorDia = {};
  combos.forEach(c => {
    const k = c.DiaSemana?.trim();
    if (!combosPorDia[k]) combosPorDia[k] = [];
    combosPorDia[k].push(c);
  });

  const guardar = async () => {
    const entries = Object.entries(form.combo).filter(([_, cid]) => cid);
    if (!form.empleadoID || !form.menuID || !entries.length) return showMsg('Selecciona empleado, menú y al menos un combo', 'err');
    setLoading(true);
    let ok = 0, err = 0;
    for (const [i, [fecha, comboID]] of entries.entries()) {
      const dia = dias.find(d => d.fecha === fecha);
      try {
        await almuerzosService.crearSolicitud({ empleadoID: parseInt(form.empleadoID), menuID: parseInt(form.menuID), comboID: parseInt(comboID), fechaAlmuerzo: fecha, diaSemana: dia?.nombre || '', tipoComida: form.tipoComida, esManual: true, creadoPor: usuarioID, enviarConfirmacion: i === entries.length - 1 });
        ok++;
      } catch { err++; }
    }
    showMsg(`${ok} solicitud(es) registrada(s)${err ? `. ${err} fallaron.` : ''}`, ok > 0 ? 'ok' : 'err');
    setLoading(false);
    setForm(f => ({ ...f, combo:{} }));
  };

  return (
    <div style={S.card}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem', marginBottom:'1.25rem' }}>
        <h3 style={{ margin:0, fontWeight:'700', color:'#1f2937' }}>Solicitud Manual por DTHS</h3>
        <div style={{ display:'flex', gap:'0.25rem', background:'#f3f4f6', padding:'0.25rem', borderRadius:'0.5rem' }}>
          {[{ id:'individual', label:'Un colaborador' }, { id:'masiva', label:'Carga masiva (Excel)' }].map(m => (
            <button key={m.id} onClick={() => setModo(m.id)} style={{ padding:'0.4rem 0.9rem', borderRadius:'0.4rem', border:'none', background: modo===m.id?'white':'transparent', color: modo===m.id?'#2563eb':'#6b7280', fontWeight: modo===m.id?'700':'500', boxShadow: modo===m.id?'0 1px 3px rgba(0,0,0,0.1)':'none', cursor:'pointer', fontSize:'0.82rem', display:'inline-flex', alignItems:'center', gap:'0.35rem' }}>
              {m.id === 'masiva' && <IcoExcelUp size={14} />}{m.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'1rem', marginBottom:'1.25rem' }}>
        {modo === 'individual' && <div>
          <label style={S.label}>Empleado</label>
          <SearchableSelect
            value={form.empleadoID}
            onChange={v => setForm(f => ({ ...f, empleadoID: v }))}
            options={empleados.map(e => ({ id: e.EmpleadoID, label: `${e.NombreCompleto} (${e.Codigo})` }))}
            placeholder="Buscar empleado..."
            emptyLabel="-- Seleccionar --"
            inputStyle={S.input}
          />
        </div>}
        <div>
          <label style={S.label}>Tipo de Comida</label>
          <select style={S.input} value={form.tipoComida} onChange={e => setForm(f => ({ ...f, tipoComida:e.target.value, combo:{} }))}>
            {TIPOS_MENU.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label style={S.label}>Menú Activo</label>
          <select style={S.input} value={form.menuID} onChange={e => onMenuChange(e.target.value)}>
            <option value="">-- Seleccionar --</option>
            {menus.filter(m => m.TipoMenu === form.tipoComida).map(m => <option key={m.MenuID} value={m.MenuID}>Semana {m.Semana} ({m.Año})</option>)}
          </select>
        </div>
      </div>
      {modo === 'masiva' ? (
        menuSel
          ? <CargaMasivaSolicitudes menu={menuSel} showMsg={showMsg} />
          : (
            <div style={{ textAlign:'center', padding:'2rem', background:'#f3f4f6', borderRadius:'0.75rem' }}>
              <p style={{ color:'#6b7280', margin:0 }}>Seleccione un tipo de comida y el menú al que se cargará el listado.</p>
            </div>
          )
      ) : menuSel ? (
        <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:'1.25rem' }}>
          <h4 style={{ margin:'0 0 1rem 0', color:'#1f2937', fontWeight:'700' }}>Selección de platos:</h4>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(250px,1fr))', gap:'1rem', marginBottom:'1.5rem' }}>
            {dias.map(d => {
              const combosDelDia = combosPorDia[d.nombre] || [];
              const isSelected = !!form.combo[d.fecha];
              return (
                <div key={d.fecha} style={{ border: isSelected?'2px solid #2563eb':'1.5px solid #e5e7eb', borderRadius:'0.75rem', padding:'1rem', background: isSelected?'#eff6ff':'#f9fafb' }}>
                  <div style={{ fontWeight:'700', color:'#1f2937', marginBottom:'0.5rem' }}>{d.nombre}<div style={{ fontWeight:'400', color:'#6b7280', fontSize:'0.75rem' }}>{fmtFecha(d.fecha)}</div></div>
                  {combosDelDia.length === 0
                    ? <span style={{ color:'#9ca3af', fontSize:'0.8rem' }}>Sin combos</span>
                    : <select style={{ ...S.input, padding:'0.5rem' }} value={form.combo[d.fecha] || ''} onChange={e => setForm(f => ({ ...f, combo:{ ...f.combo, [d.fecha]: e.target.value } }))}>
                        <option value="">-- Sin solicitud --</option>
                        {combosDelDia.map(c => <option key={c.ComboID} value={c.ComboID}>{c.NombreCombo}</option>)}
                      </select>
                  }
                </div>
              );
            })}
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <button onClick={guardar} disabled={loading || !Object.values(form.combo).some(v => v)} style={{...S.btn(), minWidth:'200px'}}>{loading ? 'Procesando...' : 'Registrar Almuerzos'}</button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign:'center', padding:'2rem', background:'#f3f4f6', borderRadius:'0.75rem' }}>
          <p style={{ color:'#6b7280', margin:0 }}>Seleccione un tipo de comida y un menú.</p>
        </div>
      )}
    </div>
  );
}

// ── Carga masiva: Excel con colaboradores y el combo de cada día ──
function CargaMasivaSolicitudes({ menu, showMsg }) {
  const [archivo, setArchivo]         = useState(null);
  const [inputKey, setInputKey]       = useState(0);
  const [validando, setValidando]     = useState(false);
  const [registrando, setRegistrando] = useState(false);
  const [preview, setPreview]         = useState(null);
  const [error, setError]             = useState('');
  const [resultado, setResultado]     = useState(null);
  const [enviarCorreos, setEnviarCorreos] = useState(true);

  const reiniciar = () => { setArchivo(null); setPreview(null); setError(''); setInputKey(k => k + 1); };
  useEffect(() => { reiniciar(); setResultado(null); }, [menu.MenuID]);

  const descargarPlantilla = async () => {
    try { await almuerzosService.descargarPlantillaSolicitudes(menu.MenuID); }
    catch (e) { showMsg(e.response?.data?.error || 'Error al descargar la plantilla', 'err'); }
  };

  const validar = async () => {
    if (!archivo) return;
    setValidando(true); setError(''); setPreview(null); setResultado(null);
    try { setPreview(await almuerzosService.cargarSolicitudesDesdeExcel(menu.MenuID, archivo, true)); }
    catch (e) { setError(e.response?.data?.error || 'Error al validar el archivo'); }
    setValidando(false);
  };

  const registrar = async () => {
    setRegistrando(true); setError('');
    try {
      const r = await almuerzosService.cargarSolicitudesDesdeExcel(menu.MenuID, archivo, false, enviarCorreos);
      setResultado(r);
      showMsg(r.message);
      reiniciar();
    } catch (e) { setError(e.response?.data?.error || 'Error al registrar las solicitudes'); }
    setRegistrando(false);
  };

  const filasPorDia = preview ? Object.entries(preview.resumen.porDia).flatMap(([dia, combos]) =>
    Object.entries(combos).map(([combo, cantidad]) => ({ dia, combo, cantidad }))) : [];

  return (
    <div style={{ borderTop:'1px solid #e5e7eb', paddingTop:'1.25rem' }}>
      <div style={{ background:'#eff6ff', borderRadius:'0.5rem', padding:'0.75rem 1rem', marginBottom:'1.25rem', fontSize:'0.85rem', color:'#1d4ed8' }}>
        Menú <strong>Semana {menu.Semana}/{menu.Año}</strong> · {fmtFecha(menu.FechaInicio)} al {fmtFecha(menu.FechaFin)}.
        Las selecciones cargadas se registran como solicitudes manuales de DTHS y aparecen en Solicitudes y en todos los reportes.
        Si un colaborador ya tenía selección para ese día, se reemplaza.
      </div>

      <div style={{ display:'grid', gap:'1rem', marginBottom:'1rem' }}>
        <div>
          <div style={{ fontWeight:'700', color:'#374151', fontSize:'0.875rem', marginBottom:'0.35rem' }}>1. Descarga la plantilla de este menú</div>
          <p style={{ margin:'0 0 0.5rem 0', color:'#6b7280', fontSize:'0.8rem' }}>
            Trae a todos los colaboradores activos (ID, cédula, nombre y departamento) y una columna por día con lista desplegable de combos; la hoja "Menú" muestra los platos.
            Completa solo a quienes almuerzan; las filas en blanco se ignoran. También puedes pegar tu propio listado usando la cédula.
          </p>
          <button onClick={descargarPlantilla} style={{ ...S.btn('#6b7280', true), fontSize:'0.8rem', padding:'0.4rem 0.85rem' }}>
            <IcoExcel /> Descargar plantilla
          </button>
        </div>
        <div>
          <div style={{ fontWeight:'700', color:'#374151', fontSize:'0.875rem', marginBottom:'0.35rem' }}>2. Sube el archivo completado y revisa la vista previa</div>
          <div style={{ display:'flex', gap:'0.75rem', alignItems:'center', flexWrap:'wrap' }}>
            <input key={inputKey} type="file" accept=".xlsx" onChange={e => { setArchivo(e.target.files?.[0] || null); setPreview(null); setError(''); }} style={{ fontSize:'0.8rem' }} />
            <button onClick={validar} disabled={!archivo || validando} style={{ ...S.btn('#2563eb'), fontSize:'0.8rem', padding:'0.4rem 0.85rem' }}>
              {validando ? 'Validando...' : 'Validar archivo'}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:'0.5rem', padding:'0.65rem 0.9rem', color:'#b91c1c', fontSize:'0.85rem', marginBottom:'1rem' }}>
          <strong>No se puede cargar el archivo:</strong> {error}
        </div>
      )}

      {resultado && (
        <div style={{ background:'#ecfdf5', border:'1px solid #a7f3d0', borderRadius:'0.5rem', padding:'0.65rem 0.9rem', color:'#065f46', fontSize:'0.85rem', marginBottom:'1rem' }}>
          <strong>Carga completada:</strong> {resultado.message}.
        </div>
      )}

      {preview && (
        <div style={{ border:'1px solid #e5e7eb', borderRadius:'0.75rem', padding:'1rem' }}>
          <div style={{ fontWeight:'700', color:'#1f2937', marginBottom:'0.75rem' }}>Vista previa</div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'0.75rem', marginBottom:'1rem' }}>
            {[
              { label:'Colaboradores', valor: preview.resumen.colaboradores, color:'#2563eb' },
              { label:'Selecciones a registrar', valor: preview.resumen.solicitudes, color:'#16a34a' },
              { label:'Reemplazan una anterior', valor: preview.resumen.reemplazos, color:'#f59e0b' },
              { label:'Con error (se omiten)', valor: preview.totalErrores, color: preview.totalErrores ? '#ef4444' : '#6b7280' },
            ].map(k => (
              <div key={k.label} style={{ background:'#f9fafb', border:'1px solid #e5e7eb', borderRadius:'0.5rem', padding:'0.65rem 0.85rem' }}>
                <div style={{ fontSize:'1.4rem', fontWeight:'800', color:k.color }}>{k.valor}</div>
                <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>{k.label}</div>
              </div>
            ))}
          </div>

          {filasPorDia.length > 0 && (
            <div style={{ overflowX:'auto', marginBottom:'1rem' }}>
              <table style={S.table}>
                <thead><tr>{['Día','Combo','Cantidad'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {filasPorDia.map((f, i) => (
                    <tr key={i}><td style={S.td}>{f.dia}</td><td style={S.td}>{f.combo}</td><td style={{ ...S.td, fontWeight:'700' }}>{f.cantidad}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.totalErrores > 0 && (
            <div style={{ marginBottom:'1rem' }}>
              <div style={{ fontWeight:'700', color:'#b91c1c', fontSize:'0.85rem', marginBottom:'0.4rem' }}>
                Filas con error ({preview.totalErrores}{preview.totalErrores > preview.errores.length ? `, se muestran ${preview.errores.length}` : ''}). Corrígelas en el Excel y vuelve a validar, o continúa y se omitirán.
              </div>
              <div style={{ maxHeight:'260px', overflowY:'auto', border:'1px solid #fecaca', borderRadius:'0.5rem' }}>
                <table style={S.table}>
                  <thead><tr>{['Fila','Colaborador','Detalle'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {preview.errores.map((e, i) => (
                      <tr key={i}><td style={S.td}>{e.fila}</td><td style={S.td}>{e.colaborador}</td><td style={{ ...S.td, color:'#b91c1c' }}>{e.mensaje}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', fontSize:'0.85rem', color:'#374151', marginBottom:'0.75rem', cursor:'pointer' }}>
            <input type="checkbox" checked={enviarCorreos} onChange={e => setEnviarCorreos(e.target.checked)} />
            Enviar a cada colaborador un correo de confirmación con su selección
          </label>
          <div style={{ display:'flex', gap:'0.75rem', justifyContent:'flex-end', flexWrap:'wrap' }}>
            <button onClick={reiniciar} disabled={registrando} style={S.btn('#6b7280', true)}>Cancelar</button>
            <button onClick={registrar} disabled={registrando || !preview.resumen.solicitudes} style={S.btn('#16a34a')}>
              {registrando ? 'Registrando...' : `Registrar ${preview.resumen.solicitudes} selección(es)`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ============================================================
// VER TODAS LAS SOLICITUDES
// ============================================================
function TabSolicitudes({ showMsg, esRRHH }) {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [semanaFiltro, setSemanaFiltro] = useState(getSemanaActual());
  const [añoFiltro, setAñoFiltro]       = useState(new Date().getFullYear());
  const [filtros, setFiltros]         = useState({
    ...rangoSemana(getSemanaActual(), new Date().getFullYear()),
    estado:'', tipo:'',
  });

  // Al elegir semana/año se precargan Desde y Hasta; las fechas se pueden seguir ajustando a mano
  const elegirSemana = (semana, año) => {
    setSemanaFiltro(semana);
    setAñoFiltro(año);
    if (semana >= 1 && semana <= 53 && año >= 2000) setFiltros(f => ({ ...f, ...rangoSemana(semana, año) }));
  };

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.fechaInicio) params.fechaInicio = filtros.fechaInicio;
      if (filtros.fechaFin)    params.fechaFin    = filtros.fechaFin;
      if (filtros.estado)      params.estado      = filtros.estado;
      if (filtros.tipo)        params.tipo        = filtros.tipo;
      setSolicitudes(await almuerzosService.getSolicitudes(params));
    } catch { setSolicitudes([]); }
    setLoading(false);
  };
  useEffect(() => { cargar(); }, []);

  const cancelar = async id => {
    if (!window.confirm('¿Cancelar esta solicitud?')) return;
    try { await almuerzosService.cancelarSolicitud(id, 'Cancelado por DTHS'); showMsg('Solicitud cancelada'); cargar(); }
    catch { showMsg('Error al cancelar', 'err'); }
  };

  const datosExport = solicitudes.map(s => ({
    Fecha: fmtFecha(s.FechaAlmuerzo), Día: s.DiaSemana, Empleado: s.NombreEmpleado,
    Código: s.CodigoEmpleado, Combo: s.Combo, Tipo: s.TipoComida, Estado: s.Estado,
  }));

  return (
    <div>
      <div style={S.card}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.75rem', marginBottom:'0.75rem' }}>
          <div><label style={S.label}>Semana #</label><input type="number" style={S.input} min={1} max={53} value={semanaFiltro} onChange={e => elegirSemana(parseInt(e.target.value, 10), añoFiltro)} /></div>
          <div><label style={S.label}>Año</label><input type="number" style={S.input} value={añoFiltro} onChange={e => elegirSemana(semanaFiltro, parseInt(e.target.value, 10))} /></div>
          <div><label style={S.label}>Desde</label><input type="date" style={S.input} value={filtros.fechaInicio} onChange={e => setFiltros(f => ({ ...f, fechaInicio:e.target.value }))} /></div>
          <div><label style={S.label}>Hasta</label><input type="date" style={S.input} value={filtros.fechaFin} onChange={e => setFiltros(f => ({ ...f, fechaFin:e.target.value }))} /></div>
          <div><label style={S.label}>Estado</label>
            <select style={S.input} value={filtros.estado} onChange={e => setFiltros(f => ({ ...f, estado:e.target.value }))}>
              <option value="">Todos</option><option value="Confirmado">Confirmado</option><option value="Cancelado">Cancelado</option>
            </select>
          </div>
          <div><label style={S.label}>Tipo</label>
            <select style={S.input} value={filtros.tipo} onChange={e => setFiltros(f => ({ ...f, tipo:e.target.value }))}>
              <option value="">Todos</option>{TIPOS_MENU.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
        </div>
        <button onClick={cargar} style={S.btn()}>Filtrar</button>
      </div>
      <div style={S.card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
          <h3 style={{ margin:0, fontWeight:'700', color:'#1f2937' }}>Solicitudes ({solicitudes.length})</h3>
          {solicitudes.length > 0 && (
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <BtnExport onClick={() => exportToExcel(datosExport, 'solicitudes-almuerzo', 'Solicitudes de Almuerzo')} icon={<IcoExcel />} label="Excel" color="#16a34a" />
              <BtnExport onClick={() => exportToPDF('tabla-solicitudes', 'solicitudes-almuerzo', 'Solicitudes de Almuerzo')} icon={<IcoPDF />} label="PDF" color="#dc2626" />
              <BtnExport onClick={() => exportToCSV(datosExport, 'solicitudes-almuerzo')} icon={<IcoCSV />} label="CSV" color="#2563eb" />
            </div>
          )}
        </div>
        {loading ? <p style={{ color:'#6b7280' }}>Cargando...</p>
          : !solicitudes.length ? <p style={{ color:'#6b7280' }}>No hay solicitudes.</p>
          : (
            <div style={{ overflowX:'auto' }}>
              <table id="tabla-solicitudes" style={S.table}>
                <thead><tr>{['Fecha','Día','Empleado','Código','Combo','Tipo','Estado','Manual','Acción'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {solicitudes.map(s => (
                    <tr key={s.SolicitudID} style={{ background: s.Estado==='Cancelado'?'#fef2f2':'white' }}>
                      <td style={S.td}>{fmtFecha(s.FechaAlmuerzo)}</td>
                      <td style={S.td}>{s.DiaSemana}</td>
                      <td style={S.td}>{s.NombreEmpleado}</td>
                      <td style={S.td}><code>{s.CodigoEmpleado}</code></td>
                      <td style={S.td}>{s.Combo}</td>
                      <td style={S.td}>{s.TipoComida}</td>
                      <td style={S.td}><span style={S.badge(s.Estado==='Confirmado'?'#16a34a':'#ef4444')}>{s.Estado}</span></td>
                      <td style={S.td}>{s.EsManual ? (s.Observaciones === 'Carga masiva Excel' ? 'Carga masiva' : 'Manual') : 'Auto'}</td>
                      <td style={S.td}>{s.Estado==='Confirmado' && esRRHH && <button onClick={() => cancelar(s.SolicitudID)} style={{ ...S.btn('#ef4444', true), fontSize:'0.75rem', padding:'0.25rem 0.6rem' }}>Cancelar</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
}

// ============================================================
// REPORTES
// ============================================================
function TabReportes() {
  const [subtab, setSubtab]         = useState('semanal');
  const [semana, setSemana]         = useState(getSemanaActual());
  const [año, setAño]               = useState(new Date().getFullYear());
  const [tipo, setTipo]             = useState('Almuerzo');
  const [data, setData]             = useState(null);
  const [reporteGen, setReporteGen] = useState([]);
  const [loadingGen, setLoadingGen] = useState(false);
  const [filtrosGen, setFiltrosGen] = useState({
    fechaInicio: isoLocal(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
    fechaFin: isoLocal(new Date()),
    estado:'', tipo:'',
  });

  const cargarSemanal = async () => {
    try { setData(await almuerzosService.getReporteSemanal(semana, año, tipo)); } catch { setData(null); }
  };
  const cargarGeneral = async () => {
    setLoadingGen(true);
    try {
      const params = {};
      if (filtrosGen.fechaInicio) params.fechaInicio = filtrosGen.fechaInicio;
      if (filtrosGen.fechaFin)    params.fechaFin    = filtrosGen.fechaFin;
      if (filtrosGen.estado)      params.estado      = filtrosGen.estado;
      if (filtrosGen.tipo)        params.tipo        = filtrosGen.tipo;
      setReporteGen(await almuerzosService.getReporteGeneral(params));
    } catch { setReporteGen([]); }
    setLoadingGen(false);
  };

  useEffect(() => { if (subtab !== 'general') cargarSemanal(); }, [subtab, semana, año, tipo]);
  useEffect(() => { if (subtab === 'general') cargarGeneral(); }, [subtab]);

  const lunes = getLunesDeSemana(semana, año);
  const periodLabel = `Semana ${semana}/${año} — ${fmtFecha(fmtD(lunes))}`;

  const reportePorDia = {};
  if (data?.filas) data.filas.forEach(f => { if (!reportePorDia[f.Dia]) reportePorDia[f.Dia] = []; reportePorDia[f.Dia].push(f); });

  const datosReporteSemanal = data?.filas?.map(f => ({ Día: f.Dia, Combo: f.Combo, Descripción: f.Descripcion || '', Cantidad: f.Total })) || [];
  const datosReporteGeneral = reporteGen.map(r => ({ Fecha: r.Fecha, Día: r.Dia, Combo: r.ComboSeleccionado, Código: r.CodigoEmpleado, Nombre: r.NombreCompleto, Tipo: r.TipoComida, Estado: r.Estado, Origen: r.Origen }));

  const subtabs = [
    { id:'semanal',  label:'Semanal Interno',  icon:<IcoReportes /> },
    { id:'oferente', label:'Para Oferente',     icon:<IcoPublicar /> },
    { id:'general',  label:'Reporte General',   icon:<IcoSolicitudes /> },
  ];

  return (
    <div>
      <div style={{ display:'flex', gap:'0.4rem', marginBottom:'1rem', flexWrap:'wrap' }}>
        {subtabs.map(t => (
          <button key={t.id} onClick={() => setSubtab(t.id)} style={{ padding:'0.4rem 1rem', borderRadius:'0.5rem', border:'none', background: subtab===t.id?'#0a2540':'#e5e7eb', color: subtab===t.id?'white':'#374151', fontWeight:'600', cursor:'pointer', fontSize:'0.8rem', display:'inline-flex', alignItems:'center', gap:'0.4rem' }}>
            {t.icon}{t.label}
          </button>
        ))}
      </div>

      {subtab !== 'general' && (
        <div style={S.card}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:'0.75rem', marginBottom:'0.75rem' }}>
            <div><label style={S.label}>Semana #</label><input type="number" style={S.input} min={1} max={53} value={semana} onChange={e => setSemana(parseInt(e.target.value))} /></div>
            <div><label style={S.label}>Año</label><input type="number" style={S.input} value={año} onChange={e => setAño(parseInt(e.target.value))} /></div>
            <div><label style={S.label}>Tipo</label>
              <select style={S.input} value={tipo} onChange={e => setTipo(e.target.value)}>
                {TIPOS_MENU.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <button onClick={cargarSemanal} style={S.btn()}>Actualizar</button>
        </div>
      )}

      {(subtab === 'semanal' || subtab === 'oferente') && data && (
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
            <h3 style={{ margin:0, fontWeight:'700', color:'#1f2937' }}>
              {subtab === 'semanal' ? 'Reporte Semanal' : 'Reporte para Oferente'} — {periodLabel}
            </h3>
            {datosReporteSemanal.length > 0 && (
              <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                <BtnExport onClick={() => exportToExcel(datosReporteSemanal, `reporte-semana-${semana}-${año}`, `Reporte Semana ${semana}/${año}`)} icon={<IcoExcel />} label="Excel" color="#16a34a" />
                <BtnExport onClick={() => exportToPDF('tabla-reporte-semanal', `reporte-semana-${semana}-${año}`, `Reporte Semana ${semana}/${año}`)} icon={<IcoPDF />} label="PDF" color="#dc2626" />
                <BtnExport onClick={() => exportToCSV(datosReporteSemanal, `reporte-semana-${semana}-${año}`)} icon={<IcoCSV />} label="CSV" color="#2563eb" />
              </div>
            )}
          </div>
          {Object.keys(reportePorDia).length === 0 ? <p style={{ color:'#6b7280' }}>Sin solicitudes.</p>
            : (
              <div id="tabla-reporte-semanal">
                {DIAS_SEMANA.filter(d => reportePorDia[d]).map(dia => (
                  <div key={dia} style={{ marginBottom:'1.25rem' }}>
                    <div style={{ background:'#1f2937', color:'white', padding:'0.5rem 1rem', borderRadius:'0.5rem 0.5rem 0 0', fontWeight:'700', fontSize:'0.875rem', display:'flex', justifyContent:'space-between' }}>
                      <span>{dia}</span><span>Total: {data.totalesPorDia?.[dia] || 0}</span>
                    </div>
                    <table style={{ ...S.table, border:'1px solid #e5e7eb', borderTop:'none' }}>
                      <thead>
                        <tr>
                          <th style={S.th}>Combo</th>
                          {subtab === 'semanal' && <th style={{ ...S.th, textAlign:'center' }}>Descripción</th>}
                          <th style={{ ...S.th, textAlign:'center', width:'80px' }}>Cant.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportePorDia[dia].map((f, i) => (
                          <tr key={i} style={{ background: i%2===0?'white':'#f9fafb' }}>
                            <td style={{ ...S.td, fontWeight:'700' }}>{f.Combo}</td>
                            {subtab === 'semanal' && <td style={{ ...S.td, fontSize:'0.8rem', color:'#6b7280' }}>{f.Descripcion || '—'}</td>}
                            <td style={{ ...S.td, textAlign:'center', fontWeight:'800', color:'#2563eb', fontSize:'1.1rem' }}>{f.Total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            )}
          {data.totalGeneral > 0 && <div style={{ textAlign:'right', padding:'0.5rem', background:'#eff6ff', borderRadius:'0.5rem', fontWeight:'700', color:'#1d4ed8' }}>Total general: {data.totalGeneral}</div>}
        </div>
      )}

      {subtab === 'general' && (
        <div>
          <div style={S.card}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.75rem', marginBottom:'0.75rem' }}>
              <div><label style={S.label}>Desde</label><input type="date" style={S.input} value={filtrosGen.fechaInicio} onChange={e => setFiltrosGen(f => ({ ...f, fechaInicio:e.target.value }))} /></div>
              <div><label style={S.label}>Hasta</label><input type="date" style={S.input} value={filtrosGen.fechaFin} onChange={e => setFiltrosGen(f => ({ ...f, fechaFin:e.target.value }))} /></div>
              <div><label style={S.label}>Estado</label>
                <select style={S.input} value={filtrosGen.estado} onChange={e => setFiltrosGen(f => ({ ...f, estado:e.target.value }))}>
                  <option value="">Todos</option><option value="Confirmado">Confirmado</option><option value="Cancelado">Cancelado</option>
                </select>
              </div>
              <div><label style={S.label}>Tipo</label>
                <select style={S.input} value={filtrosGen.tipo} onChange={e => setFiltrosGen(f => ({ ...f, tipo:e.target.value }))}>
                  <option value="">Todos</option>{TIPOS_MENU.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </div>
            </div>
            <button onClick={cargarGeneral} disabled={loadingGen} style={S.btn()}>{loadingGen ? 'Cargando...' : 'Generar Reporte'}</button>
          </div>
          <div style={S.card}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem', flexWrap:'wrap', gap:'0.5rem' }}>
              <h3 style={{ margin:0, fontWeight:'700', color:'#1f2937' }}>Reporte General ({reporteGen.length} registros)</h3>
              {reporteGen.length > 0 && (
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <BtnExport onClick={() => exportToExcel(datosReporteGeneral, 'reporte-general-almuerzo', 'Reporte General de Almuerzos')} icon={<IcoExcel />} label="Excel" color="#16a34a" />
                  <BtnExport onClick={() => exportToPDF('tabla-reporte-general', 'reporte-general-almuerzo', 'Reporte General de Almuerzos')} icon={<IcoPDF />} label="PDF" color="#dc2626" />
                  <BtnExport onClick={() => exportToCSV(datosReporteGeneral, 'reporte-general-almuerzo')} icon={<IcoCSV />} label="CSV" color="#2563eb" />
                </div>
              )}
            </div>
            {loadingGen ? <p style={{ color:'#6b7280' }}>Cargando...</p>
              : !reporteGen.length ? <p style={{ color:'#6b7280' }}>Sin registros.</p>
              : (
                <div style={{ overflowX:'auto' }}>
                  <table id="tabla-reporte-general" style={S.table}>
                    <thead><tr>{['Fecha','Día','Combo','Código','Nombre','Tipo','Estado','Origen'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                    <tbody>
                      {reporteGen.map((r, i) => (
                        <tr key={i} style={{ background: r.Estado==='Cancelado'?'#fef2f2': i%2===0?'white':'#f9fafb' }}>
                          <td style={{ ...S.td, fontWeight:'600' }}>{r.Fecha}</td>
                          <td style={S.td}>{r.Dia}</td>
                          <td style={{ ...S.td, fontWeight:'600', color:'#2563eb' }}>{r.ComboSeleccionado}</td>
                          <td style={S.td}><code style={{ fontSize:'0.8rem', background:'#f3f4f6', padding:'0.1rem 0.35rem', borderRadius:'0.25rem' }}>{r.CodigoEmpleado}</code></td>
                          <td style={{ ...S.td, fontWeight:'500' }}>{r.NombreCompleto}</td>
                          <td style={S.td}>{r.TipoComida}</td>
                          <td style={S.td}><span style={S.badge(r.Estado==='Confirmado'?'#16a34a':'#ef4444')}>{r.Estado}</span></td>
                          <td style={S.td}><span style={S.badge(r.Origen==='Carga masiva DTHS'?'#8b5cf6':r.Origen==='Manual DTHS'?'#f59e0b':'#6b7280')}>{r.Origen}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// MI SOLICITUD
// ============================================================
// ============================================================
// MI SOLICITUD
// ============================================================
function TabMiSolicitud({ showMsg, empleadoID, esRRHH = false }) {
  const [menuSel, setMenuSel]                             = useState(null);
  const [combos, setCombos]                               = useState([]);
  const [tipoComida, setTipoComida]                       = useState('Almuerzo');
  const [selecciones, setSelecciones]                     = useState({});  // solo local hasta enviar
  const [solicitudesExistentes, setSolicitudesExistentes] = useState([]);
  const [enviando, setEnviando]                           = useState(false);
  const [loading, setLoading]                             = useState(true);
  const [menuCerrado, setMenuCerrado]                     = useState(false);
  const [modoLectura, setModoLectura]                     = useState(false);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      const ms        = await almuerzosService.getMenus({ estado: 'Activo' });
      const activo    = ms.find(m => m.TipoMenu === tipoComida);
      const todosMenus = await almuerzosService.getMenus();
      const menu      = activo || todosMenus.filter(m => m.TipoMenu === tipoComida)[0];

      if (menu) {
        setMenuSel(menu);
        setMenuCerrado(!activo);
        const [cs, sols] = await Promise.all([
          almuerzosService.getCombosDeMenu(menu.MenuID),
          almuerzosService.getMisSolicitudes(empleadoID, { menuID: menu.MenuID }),
        ]);
        setCombos(cs);
        setSolicitudesExistentes(sols);

        const confirmadas = sols.filter(s => s.Estado !== 'Cancelado');
        if (confirmadas.length > 0) {
          // Ya tiene solicitudes → modo lectura directo
          const mapeo = {};
          confirmadas.forEach(s => {
            mapeo[fechaLocalDesdeISO(s.FechaAlmuerzo)] = String(s.ComboID);
          });
          setSelecciones(mapeo);
          setModoLectura(true);
        } else {
          setSelecciones({});
          setModoLectura(false);
        }
      } else {
        setMenuSel(null); setCombos([]); setSolicitudesExistentes([]);
        setSelecciones({}); setModoLectura(false);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      setMenuSel(null);
    }
    setLoading(false);
  }, [empleadoID, tipoComida]);

  useEffect(() => { cargarDatos(); }, [cargarDatos]);

  // ── Selección local sin llamar al API ────────────────────────
  const toggleCombo = (fecha, comboID) => {
    if (modoLectura || menuCerrado) return;
    setSelecciones(prev => {
      if (prev[fecha] === String(comboID)) {
        const n = { ...prev }; delete n[fecha]; return n;
      }
      return { ...prev, [fecha]: String(comboID) };
    });
  };

  // ── Enviar todas las selecciones al backend de una vez ───────
  const enviarSolicitud = async () => {
    const entries = Object.entries(selecciones).filter(([_, cid]) => cid);
    if (!entries.length) return;
    setEnviando(true);
    try {
      const tipoConfig = TIPOS_MENU.find(t => t.key === tipoComida);
      const dias = calcDias(menuSel.FechaInicio, tipoConfig?.dias || DIAS_SEMANA);
      let ok = 0;
      for (const [i, [fecha, comboID]] of entries.entries()) {
        const dia = dias.find(d => d.fecha === fecha);
        await almuerzosService.crearSolicitud({
          empleadoID:   parseInt(empleadoID),
          menuID:       menuSel.MenuID,
          comboID:      parseInt(comboID),
          fechaAlmuerzo: fecha,
          diaSemana:    dia?.nombre || '',
          tipoComida,
          esManual:     false,
          // Un solo correo de confirmación (con toda la semana) al registrar el último día
          enviarConfirmacion: i === entries.length - 1,
        });
        ok++;
      }
      showMsg(`Solicitud enviada — ${ok} día${ok > 1 ? 's' : ''} confirmado${ok > 1 ? 's' : ''}`);
      setModoLectura(true);
      const nuevasSols = await almuerzosService.getMisSolicitudes(empleadoID, { menuID: menuSel.MenuID });
      setSolicitudesExistentes(nuevasSols);
    } catch (e) {
      console.error('Error enviando solicitud:', e);
      showMsg('Error al enviar la solicitud.', 'err');
    }
    setEnviando(false);
  };

  if (loading) return <div style={S.card}><p style={{ color:'#6b7280', margin:0 }}>Cargando menú disponible...</p></div>;

  const tipoConfig     = TIPOS_MENU.find(t => t.key === tipoComida);
  const dias           = menuSel ? calcDias(menuSel.FechaInicio, tipoConfig?.dias || DIAS_SEMANA) : [];
  const totalSel       = Object.values(selecciones).filter(v => v).length;
  const puedeEditar    = !modoLectura && !menuCerrado;

  return (
    <div>
      {/* ── Selector tipo comida ── */}
      <div style={{ ...S.card, padding:'1rem 1.5rem' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
          <label style={{ ...S.label, margin:0 }}>Tipo de Comida:</label>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            {TIPOS_MENU.filter(t => esRRHH || t.key === 'Almuerzo').map(t => (
              <button key={t.key} onClick={() => { setTipoComida(t.key); }}
                style={{ padding:'0.4rem 1rem', borderRadius:'0.5rem', border:'none',
                  background: tipoComida === t.key ? '#0a2540' : '#f3f4f6',
                  color:      tipoComida === t.key ? 'white'   : '#374151',
                  fontWeight:'600', fontSize:'0.8rem', cursor:'pointer' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Banner estado menú ── */}
      {!menuSel ? (
        <div style={{ ...S.card, background:'#fef9c3', border:'1.5px solid #fde047' }}>
          <p style={{ margin:0, color:'#854d0e', fontWeight:'600' }}>
            No hay menú publicado para {tipoConfig?.label}.
          </p>
        </div>
      ) : (
        <div style={{
          ...S.card,
          background: menuCerrado ? '#fef2f2' : modoLectura ? '#eff6ff' : '#f0fdf4',
          border:     menuCerrado ? '1.5px solid #fecaca' : modoLectura ? '1.5px solid #bfdbfe' : '1.5px solid #bbf7d0',
          padding:'0.875rem 1.5rem',
        }}>
          <p style={{ margin:0, fontWeight:'600', fontSize:'0.875rem',
            color: menuCerrado ? '#991b1b' : modoLectura ? '#1d4ed8' : '#15803d' }}>
            {menuCerrado ? <><Lock size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Proceso Cerrado</> : modoLectura ? <><ClipboardList size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Solicitud Enviada</> : <><CheckCircle size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Menú Activo</>}
            {' '}— Semana {menuSel.Semana}/{menuSel.Año} &nbsp;·&nbsp;
            {fmtFecha(menuSel.FechaInicio)} al {fmtFecha(menuSel.FechaFin)}
          </p>
        </div>
      )}

      {/* ── Tabla selección / lectura ── */}
      {menuSel && (
        <div style={S.card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1rem' }}>
            <h3 style={{ margin:0, fontSize:'1rem', fontWeight:'700', color:'#1f2937' }}>
              {modoLectura ? <><ClipboardList size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Tu Solicitud de la Semana</> : 'Cronograma de Menú'}
            </h3>
            {puedeEditar && (
              <span style={{ ...S.badge(totalSel === dias.length ? '#16a34a' : '#f59e0b'), fontSize:'0.85rem', padding:'0.3rem 0.85rem' }}>
                {totalSel}/{dias.length} Seleccionados
              </span>
            )}
          </div>

          <div style={{ overflowX:'auto' }}>
            <table style={{ ...S.table, borderCollapse:'separate', borderSpacing:'0 6px' }}>
              <thead>
                <tr style={{ background:'#f8fafc' }}>
                  <th style={{ ...S.th, borderRadius:'0.5rem 0 0 0.5rem', width:'120px' }}>Día</th>
                  {[1,2,3,4].map(i => <th key={i} style={S.th}>Opción {i}</th>)}
                  {puedeEditar && (
                    <th style={{ ...S.th, borderRadius:'0 0.5rem 0.5rem 0', width:'150px' }}>Tu Selección</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {dias.map(d => {
                  const combosDia = combos.filter(c => c.DiaSemana?.trim() === d.nombre);
                  const selID     = selecciones[d.fecha] ? String(selecciones[d.fecha]) : '';
                  const comboSel  = combos.find(c => String(c.ComboID) === selID);

                  return (
                    <tr key={d.fecha} style={{ background: selID ? '#f0fdf4' : 'white', transition:'background .2s' }}>

                      {/* Columna día */}
                      <td style={{ ...S.td,
                        borderLeft: selID ? '4px solid #16a34a' : '4px solid transparent',
                        borderRadius:'0.5rem 0 0 0.5rem', transition:'border .2s' }}>
                        <strong style={{ fontSize:'0.85rem', color: selID ? '#15803d' : '#1f2937' }}>{d.nombre}</strong><br/>
                        <small style={{ color:'#9ca3af', fontSize:'0.72rem' }}>{fmtFecha(d.fecha)}</small>
                        {selID && <div style={{ fontSize:'0.65rem', color:'#16a34a', fontWeight:'700', marginTop:'2px' }}><Check size={11} style={{ verticalAlign:'-1px', marginRight:'0.2rem' }} />SELECCIONADO</div>}
                      </td>

                      {/* Columnas opciones */}
                      {[0,1,2,3].map(i => {
                        const c      = combosDia[i];
                        const esEste = c && selID === String(c.ComboID);
                        return (
                          <td key={i}
                            onClick={() => puedeEditar && c && toggleCombo(d.fecha, c.ComboID)}
                            style={{
                              padding:'0.75rem 0.9rem',
                              verticalAlign:'top',
                              cursor:     c && puedeEditar ? 'pointer' : 'default',
                              background: esEste ? '#dcfce7' : c ? 'white' : '#fafafa',
                              border:     esEste ? '2px solid #16a34a' : '1.5px solid #e5e7eb',
                              borderRadius:'0.5rem',
                              position:'relative',
                              transition:'all .15s',
                              minWidth:'160px',
                              opacity: modoLectura && !esEste ? 0.4 : 1,
                            }}>
                            {c ? (
                              <>
                                {esEste && (
                                  <div style={{ position:'absolute', top:'7px', right:'7px',
                                    width:'20px', height:'20px', borderRadius:'50%',
                                    background:'#16a34a', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                    <Check size={12} strokeWidth={3} style={{ color:'white' }} />
                                  </div>
                                )}
                                {/* Nombre combo */}
                                <div style={{ fontWeight:'700', fontSize:'0.85rem',
                                  color: esEste ? '#15803d' : '#1f2937',
                                  paddingRight: esEste ? '26px' : '0',
                                  marginBottom:'0.35rem' }}>
                                  {c.NombreCombo}
                                </div>
                                {/* Descripción más grande */}
                                <div style={{ fontSize:'0.82rem',
                                  color: esEste ? '#16a34a' : '#6b7280',
                                  lineHeight:'1.45' }}>
                                  {c.Descripcion || ''}
                                </div>
                              </>
                            ) : (
                              <span style={{ color:'#e5e7eb', fontSize:'0.8rem' }}>—</span>
                            )}
                          </td>
                        );
                      })}

                      {/* Columna selector dropdown (solo en modo edición) */}
                      {puedeEditar && (
                        <td style={{ ...S.td, borderRadius:'0 0.5rem 0.5rem 0' }}>
                          <select value={selID}
                            onChange={e => toggleCombo(d.fecha, e.target.value)}
                            style={{ width:'100%', padding:'6px 7px', borderRadius:'6px', fontSize:'0.82rem',
                              border:      selID ? '2px solid #16a34a' : '1px solid #d1d5db',
                              background:  selID ? '#f0fdf4' : 'white',
                              color:       selID ? '#15803d' : '#374151',
                              fontWeight:  selID ? '600' : '400',
                              cursor:'pointer' }}>
                            <option value="">— Omitir —</option>
                            {combosDia.map(c => (
                              <option key={c.ComboID} value={String(c.ComboID)}>{c.NombreCombo}</option>
                            ))}
                          </select>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── Botón Enviar (solo edición, al menos 1 selección) ── */}
          {puedeEditar && totalSel > 0 && (
            <div style={{ marginTop:'1.75rem', display:'flex', justifyContent:'flex-end' }}>
              <button onClick={enviarSolicitud} disabled={enviando}
                style={{ ...S.btn('#16a34a'), padding:'0.8rem 2.5rem', fontSize:'1rem',
                  fontWeight:'700', boxShadow:'0 4px 14px rgba(22,163,74,0.3)',
                  minWidth:'240px', justifyContent:'center' }}>
                {enviando
                  ? 'Enviando...'
                  : ` Enviar Solicitud (${totalSel} día${totalSel > 1 ? 's' : ''})`}
              </button>
            </div>
          )}

          {/* ── Resumen final (modo lectura) ── */}
          {modoLectura && totalSel > 0 && (
            <div style={{ marginTop:'1.75rem', padding:'1.25rem 1.5rem',
              background:'#eff6ff', borderRadius:'0.875rem', border:'1.5px solid #bfdbfe' }}>
              <h4 style={{ margin:'0 0 1rem 0', fontSize:'0.95rem', color:'#1d4ed8', fontWeight:'700' }}>
                <ClipboardList size={14} style={{ verticalAlign:'-2px', marginRight:'0.35rem', flexShrink:0 }} />Resumen de tu solicitud:
              </h4>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(210px,1fr))', gap:'0.75rem' }}>
                {dias.map(d => {
                  const cid   = selecciones[d.fecha];
                  const combo = combos.find(c => String(c.ComboID) === cid);
                  if (!cid || !combo) return null;
                  return (
                    <div key={d.fecha} style={{ background:'white', borderRadius:'0.625rem',
                      padding:'0.85rem 1rem', border:'1.5px solid #bfdbfe',
                      display:'flex', alignItems:'flex-start', gap:'0.65rem' }}>
                      <span style={{ background:'#2563eb', color:'white', borderRadius:'50%',
                        width:'22px', height:'22px', minWidth:'22px',
                        display:'flex', alignItems:'center', justifyContent:'center',
                        fontSize:'11px', fontWeight:'900', marginTop:'2px' }}>
                        <Check size={12} strokeWidth={3} />
                      </span>
                      <div>
                        {/* Día y fecha */}
                        <div style={{ fontSize:'0.72rem', color:'#6b7280', fontWeight:'600', marginBottom:'0.2rem' }}>
                          {d.nombre} · {fmtFecha(d.fecha)}
                        </div>
                        {/* Nombre del combo */}
                        <div style={{ fontSize:'0.88rem', color:'#1d4ed8', fontWeight:'700', marginBottom:'0.2rem' }}>
                          {combo.NombreCombo}
                        </div>
                        {/* Descripción del combo */}
                        {combo.Descripcion && (
                          <div style={{ fontSize:'0.8rem', color:'#374151', lineHeight:'1.4' }}>
                            {combo.Descripcion}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}